/**
 * FC quality_mode=low runtime.
 * Translates fc-low HTTP + browser TTS into the same fc-v5-shaped AgentCommand
 * stream that FcSessionService.processCommand already handles — no WS / AgentCore.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable, Subscription, TimeoutError } from 'rxjs';
import type { AgentCommand } from '../fc-session.service';
import {
  FcLowAffectSignals,
  FcLowAnswerTimer,
  FcLowDetectedCommandHint,
  FcLowDetectedCommandType,
  FcLowEndReason,
  FcLowPhase,
  FcLowPromptAction,
  FcLowResponse,
  FcLowAction,
  FcLowApiService,
  FcLowStreamError,
  FcLowTurnStream,
  buildSubmitTurnRequest,
} from './fc-low-api.service';
import type { AffectSnapshot } from '../affect/affect.types';
import { FcBrowserTtsService } from './fc-browser-tts.service';
import {
  FC_LOW_CONNECTOR_NO_REPEAT,
  pickConnector,
  pickConnectorByCategory,
  type ConnectorKind,
} from './fc-low-connectors';
import {
  ABSENCE_VOICED_RESET_S,
  absenceNextAction,
} from './absenceLadder';
import {
  VC_GRACE_FIRST_S,
  VC_GRACE_SECOND_S,
  VC_MAX_STRIKES,
  VC_SUSPENSION_FALLBACK_TEXT,
  vcLadderNextAction,
  vcRecheckClearsStrike,
  vcWarningCopy,
} from './conferencingLadder';
import { ThinkingVoiceCommandService } from '../thinking-voice-command.service';
import { looksIncompleteTranscript } from '../speech-completeness.util';
import { VcScorerService } from 'src/app/services/vc-scorer.service';
import { VcScoreState } from 'src/app/services/watchers/vc-signals.types';
import { isApiError, reportApiError } from 'src/app/shared/utils/rum-error.util';

/** Align with fc-low CONFIG think_s (30) when API omits budgets. */
const DEFAULT_THINK_S = 30;
const DEFAULT_ANSWER_S = 120;
const EMPTY_ANSWER_FALLBACK = '(silencio)';
/** Explicit pass token — API forces advance; must NOT be (silencio). */
const PASS_TRANSCRIPT = '(comando:pass)';
/** Absence finalize token — parent fc-low maps to candidate_absent farewell. */
const ABSENT_TRANSCRIPT = '(comando:absent)';
/**
 * VC detection (CONTRATO-VC-DETECTION.md §0/§2/§4.2) — sentinel que cierra el
 * turno cuando conferencingLadder decide suspender. Mismo patrón que
 * ABSENT_TRANSCRIPT: el BE (FcLowBL.submitTurn) lo matchea literal y no lo
 * upsertea como respuesta.
 */
const CONFERENCING_TRANSCRIPT = '(comando:conferencing_tool)';
/** Align with fc-low slow-profile silence (3.5s) when API omits turn_silence_s. */
const DEFAULT_TURN_SILENCE_S = 3.5;
/**
 * Cierre de turno por silencio (EOS) ADAPTATIVO. NO hay umbrales fijos: la base
 * es `turn_silence_s` del último mic_open (escalera por perfil de ritmo del
 * candidato: very_slow 4.0 / slow 3.5 / normal 2.4 / fast 1.6 / very_fast 1.0).
 *  - final completo  → base.
 *  - final colgado (conector/muletilla/<3 palabras) → base × EOS_INCOMPLETE_FACTOR.
 * EOS_MIN_MS / EOS_MAX_MS son SOLO cotas de seguridad = extremos de esa
 * escalera (1.0–5.5 s); nunca dominan el valor adaptativo.
 */
export const EOS_MIN_MS = 1_000;
export const EOS_MAX_MS = 5_500;
export const EOS_INCOMPLETE_FACTOR = 1.5;
/**
 * Chrome reinicia la sesión de SpeechRecognition sola (hueco de 1–3 s sin
 * interims): en perfiles rápidos (fast 1.6 s / very_fast 1.0 s) ese hueco cortaba
 * al candidato. Mientras el recognizer reinicia, el EOS NO se dispara. Esta cota
 * es solo seguridad (reinicio que nunca reporta 'start'): no es un umbral de cierre.
 */
export const FC_LOW_RECOGNIZER_RESTART_MAX_MS = 5_000;
/** Re-chequeo del EOS mientras el recognizer reinicia. */
const RECOGNIZER_RESTART_POLL_MS = 250;
const CLARIFY_TRANSCRIPT = 'no entendí la pregunta';
const CONTEXT_TRANSCRIPT = 'dame contexto';
/** Comando "quiero una pausa" (piense / botón): el BE concede o declina la pausa. */
const PAUSE_TRANSCRIPT = 'quiero una pausa';
/** Continuar del modal de pausa: el BE resume dónde quedaron y repite el enunciado. */
export const PAUSE_RESUME_TRANSCRIPT = '__fc_pause_resume__';
const ABSENCE_POLL_MS = 500;
/**
 * Lightweight port of high `DEFAULT_FILLER_GATE_MS` (coordinator/filler.py).
 * After this delay while waiting on submitTurn, speak one short Spanish filler.
 * Not used on greeting name-capture (commands panel must stay visible / quiet).
 *
 * Disparo temprano (no 1500ms): el filler viejo sonaba a 1.5s = justo cuando
 * volvia submitTurn, pegado a la contrapregunta (por eso se quito, c82ef38).
 * Disparando temprano, el conector suena al INICIO del gap de ~1-2s y termina
 * antes de la respuesta. Si submitTurn vuelve antes de que el filler arranque,
 * el timer se cancela sin audio; si ya arranco, se deja terminar (no se corta
 * a media palabra) antes de narrar.
 */
export const FC_LOW_FILLER_GATE_MS = 0;
/** Piso de espera para que un filler ya iniciado termine antes de narrar. */
export const FC_LOW_FILLER_MAX_WAIT_MS = 900;
/** Duración natural estimada del TTS por carácter (es-419, rate ~1.0, con pausas de puntuación). */
export const FC_LOW_FILLER_MS_PER_CHAR = 90;
/** Latencia de arranque del TTS (voces online de Chrome tardan ~0,3-0,8 s en sonar). */
export const FC_LOW_FILLER_STARTUP_MS = 700;
/** Techo de la espera derivada del largo (TTS trabado ⇒ no bloquear la narración). */
export const FC_LOW_FILLER_MAX_WAIT_CAP_MS = 4_500;

/**
 * Espera máxima, contada desde que ARRANCÓ el conector, para que terminen `chars`
 * caracteres: arranque + chars×90 ms, en [900, 4500]. El viejo tope de 1800 ms
 * cortaba "Ya veo, gracias por contarme." cuando el BE respondía rápido.
 */
export function fillerWaitBoundMs(chars: number): number {
  return Math.min(
    FC_LOW_FILLER_MAX_WAIT_CAP_MS,
    Math.max(
      FC_LOW_FILLER_MAX_WAIT_MS,
      FC_LOW_FILLER_STARTUP_MS + Math.round(chars * FC_LOW_FILLER_MS_PER_CHAR),
    ),
  );
}
/** Pausa Q→Q para que el overlay "Pasando a la siguiente pregunta..." se vea
 *  (breve: un hueco mudo largo entre el ack y la pregunta rompe la fluidez). */
export const FC_LOW_TRANSITION_MS = 800;
/**
 * Streaming por etapas: tope de espera de `settled` (persistencia diferida del
 * BE) DESPUES de la decision. Vencido = se trata como persistencia fallida.
 */
export const FC_LOW_SETTLED_TIMEOUT_MS = 45_000;

/** Synthetic RUM `code` for a fc-low failure that is not an interceptor `ApiError` (plan §2.4 list). */
function classifyClientFailure(err: unknown): { status: number; code: string } {
  if (err instanceof HttpErrorResponse) return { status: err.status, code: 'NON_JSON_RESPONSE' }; // synthetic 502 for an invalid JSON body
  if (err instanceof TimeoutError) return { status: 0, code: 'TIMEOUT' };
  if (err instanceof FcLowStreamError) return { status: 0, code: err.code };
  if (/settled: timeout/.test((err as Error | undefined)?.message ?? '')) return { status: 0, code: 'SETTLED_TIMEOUT' };
  return { status: 0, code: 'CLIENT_ERROR' };
}

/** Redundant structured signal sent ALONGSIDE the sentinel/phrase transcript
 *  (never as a replacement) — confidence 1 because it comes from an exact
 *  registry match in ThinkingVoiceCommandService, not a fuzzy guess. */
function buildDetectedCommandHint(command: FcLowDetectedCommandType): FcLowDetectedCommandHint {
  return { command, source: 'client_regex', confidence: 1 };
}

/** reason del low-api cuando la respuesta ES un salvavidas (PLAN_LIFELINE.md §6). */
export const LIFELINE_HINT_REASON = 'lifeline_hint';
/** Cierre del módulo de experiencia previa: Q1 con el mismo contrato que greeting_complete. */
export const INTRO_COMPLETE_REASON = 'intro_complete';
/**
 * Piso del silencio de cierre (EOS) en turnos del módulo de experiencia previa:
 * el candidato narra su trayectoria y hace pausas para recordar. Se usa el
 * escalón 'slow' de la escalera (3.5 s) como mínimo, aunque el BE mande uno más corto.
 */
export const INTRO_MIN_TURN_SILENCE_S = 5;
/**
 * Piso del silencio en el turno de confirmación de avance ("¿quieres agregar algo
 * más o pasamos?"): el candidato decide antes de hablar. Sin habla en esta ventana
 * se cierra el turno vacío y el BE avanza.
 */
export const CONFIRM_ADVANCE_MIN_TURN_SILENCE_S = 4;

/** Piso de turn_silence_s según el tipo de turno (0 = sin piso). */
function turnSilenceFloorS(isIntroTurn: boolean, confirmAdvance: boolean): number {
  if (isIntroTurn) return INTRO_MIN_TURN_SILENCE_S;
  if (confirmAdvance) return CONFIRM_ADVANCE_MIN_TURN_SILENCE_S;
  return 0;
}

/** Segundos de pausa concedida por el BE (active_prompt_kind 'pause'); null si no aplica. */
function grantedPauseSeconds(res: FcLowResponse): number | null {
  if (res.active_prompt_kind !== 'pause') return null;
  if (typeof res.pause_s !== 'number' || res.pause_s <= 0) return null;
  return res.pause_s;
}

/** Segundos de prueba que quedan al conceder la pausa; null si el BE no los manda. */
function grantedSessionRemainingSeconds(res: FcLowResponse): number | null {
  if (grantedPauseSeconds(res) === null) return null;
  if (typeof res.session_remaining_s !== 'number' || res.session_remaining_s < 0) return null;
  return res.session_remaining_s;
}

/** Cola de eco tras el fin de un TTS local: el ASR entrega finales tardíos. */
export const FC_LOW_ECHO_TAIL_MS = 2_500;
/** Ventana de textos hablados recientes contra los que se compara un posible eco. */
export const FC_LOW_ECHO_RECENT_MS = 8_000;
/** Texto hablado por TTS local; `connector` = conector/acuse local (candidato a eco corto). */
interface RecentSpeech {
  text: string;
  endedAt: number | null;
  connector: boolean;
}

/** Tope de entradas en recentSpeech (higiene de memoria). */
const ECHO_MAX_ENTRIES = 12;

const MIN_WPM_DURATION_MS = 1000;
const MAX_WPM = 600;
/** Solapamiento de tokens de CONTENIDO >= este umbral con lo hablado ⇒ eco del TTS. */
const TTS_ECHO_OVERLAP = 0.6;
/** Mínimo de tokens de contenido en común para declarar eco por solapamiento/subcadena. */
const ECHO_MIN_CONTENT_TOKENS = 2;
/** Tokens de contenido: >= 3 letras y fuera de stopwords ("sin" cuenta: "sin problema" es un eco típico). */
const ECHO_MIN_TOKEN_LEN = 3;
const ECHO_STOPWORDS: ReadonlySet<string> = new Set([
  'que', 'los', 'las', 'del', 'una', 'uno', 'unos', 'unas', 'con', 'por', 'para', 'como', 'pero',
  'porque', 'cuando', 'donde', 'este', 'esta', 'esto', 'estos', 'estas', 'ese', 'esa', 'eso',
  'esos', 'esas', 'mas', 'muy', 'mis', 'tus', 'sus', 'nos', 'les', 'lee', 'ser', 'son', 'fue',
  'hay', 'han', 'has', 'era', 'ahi', 'aqui', 'ya', 'tan', 'sea', 'sobre', 'entre', 'hasta',
  'desde', 'tambien', 'solo', 'algo', 'todo', 'toda', 'todos', 'todas', 'cual', 'quien',
]);

/** Un interim/final de <= este número de tokens se compara por solapamiento total con lo hablado. */
const ECHO_SHORT_MAX_TOKENS = 3;

/** Pliega variantes de ASR/TTS de la misma muletilla ("okay" ≡ "okey" ≡ "ok"). */
function foldEchoToken(t: string): string {
  return t === 'ok' || t === 'okay' ? 'okey' : t;
}

/**
 * Frases-comando del candidato: NUNCA son eco salvo que aparezcan literales como
 * COLA de la última locución de Eva dentro de la cola de eco. "Exactas" = el
 * texto completo; "prefijo" = arranca así (queja/pedido corto, <= 6 palabras).
 */
const ECHO_COMMAND_EXACT: ReadonlySet<string> = new Set([
  'si', 'no', 'de acuerdo', 'gracias', 'siguiente', 'pasar', 'repite', 'repiteme',
]);
const ECHO_COMMAND_PREFIXES: readonly string[] = [
  'no entendi', 'no me quedo claro', 'no me queda claro', 'repite', 'repiteme',
];
const ECHO_COMMAND_PREFIX_MAX_WORDS = 6;

/** Opciones nombradas de openTurn (reemplaza el array posicional + reqArgs[9]). */
interface OpenTurnOptions {
  transcript: string;
  adaptationProfile?: string | null;
  detectedCommandHint?: FcLowDetectedCommandHint | null;
  transcriptIncomplete?: boolean;
  endReason?: FcLowEndReason;
  affectSignals?: FcLowAffectSignals | null;
  /** Greeting submit: el FE ya no habla acuse local ("Mucho gusto" lo dice el BE) => siempre false; undefined = no es greeting. */
  greetingAckSpoken?: boolean;
  /** ¿El FE habló un conector en ESTE submit? (`ack_spoken`). */
  ackSpoken?: boolean;
  ackMode?: 'server';
  /** turn_seq de la respuesta parkeada descartada (nunca narrada). */
  unheardTurnSeq?: number;
  /** Enunciado abierto. Ausente en comandos y sentinelas. */
  utteranceId?: string;
  utteranceRevision?: number;
  answerTimer?: FcLowAnswerTimer | null;
}

/** reasons del BE que, sin prompt_action (BE viejo), equivalen a 'repose'. */
const REPOSE_FALLBACK_REASONS: ReadonlySet<string> = new Set([
  'help_declined',
  'distress_contained',
  'client_ask_for_answer',
  'candidate_repeat',
  'premature_user_turn',
  'echo_suspected',
  'noop',
  'stale_turn',
]);

/**
 * Qué hizo el BE con el prompt. Usa `prompt_action` si viene; si no (BE viejo),
 * una respuesta phase=followup con reason de "no cambió nada" es 'repose'.
 */
function resolvePromptAction(res: FcLowResponse): FcLowPromptAction | null {
  if (res.prompt_action) return res.prompt_action;
  if (res.phase === 'followup' && res.reason && REPOSE_FALLBACK_REASONS.has(res.reason)) {
    return 'repose';
  }
  return null;
}

function countWords(text: string): number {
  return (text ?? '').trim().split(/\s+/).filter(Boolean).length;
}

function normalizeForEcho(text: string): string[] {
  return (text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

function contentTokens(tokens: string[]): string[] {
  return tokens.filter((t) => t.length >= ECHO_MIN_TOKEN_LEN && !ECHO_STOPWORDS.has(t));
}

function isEchoCommandPhrase(said: string[]): boolean {
  const joined = said.join(' ');
  if (ECHO_COMMAND_EXACT.has(joined)) return true;
  if (said.length > ECHO_COMMAND_PREFIX_MAX_WORDS) return false;
  return ECHO_COMMAND_PREFIXES.some((p) => joined === p || joined.startsWith(`${p} `));
}

/** ¿`said` es literalmente la cola (sufijo de tokens) de `utterance`? */
function isTailOf(said: string[], utterance: string[]): boolean {
  if (!said.length || said.length > utterance.length) return false;
  const off = utterance.length - said.length;
  return said.every((t, i) => utterance[off + i] === t);
}

/**
 * Eco corto (acústico, no semántico): <= 3 tokens, al menos uno de contenido y
 * la COLA literal (sufijo, plegado okay≡okey≡ok) de algún CONECTOR local reciente
 * ("detalle" de "Gracias por contarlo con detalle.", "acuerdo", "contarme").
 * Solo los conectores cuentan: contra la pregunta/narración NO se aplica, o se
 * tragaría respuestas genuinas ("Redis", "en el DTO", "mocks").
 * Documentado: "gracias" es cola de "Gracias." (conector completo) => eco; es
 * cabeza (no cola) de "Gracias por contarme." => no eco.
 */
function isShortEchoOfConnector(said: string[], connectors: string[][]): boolean {
  if (!said.length || said.length > ECHO_SHORT_MAX_TOKENS) return false;
  const folded = said.map(foldEchoToken);
  if (!contentTokens(folded).length) return false;
  return connectors.some((c) => isTailOf(folded, c.map(foldEchoToken)));
}

/**
 * ¿El interim del ASR es (casi) lo que Eva dijo? Solo compara tokens de CONTENIDO
 * (sin stopwords ni tokens cortos) para no tragarse respuestas reales del
 * candidato ("sí", "no entendí la pregunta") que comparten palabras funcionales.
 *  - Comandos del candidato: eco solo si son la cola literal de `lastUtterance`.
 *  - Subcadena: >= 2 tokens de contenido y literalmente dentro de `lastUtterance`.
 *  - Solapamiento: >= 2 tokens de contenido en común y >= 60% de los del candidato.
 *  - Corto (<= 3 tokens, >= 1 de contenido): eco SOLO si es cola literal de un conector (`connectors`);
 *    los textos cortos nunca caen por subcadena/solapamiento con la narración. Sin `connectors`
 *    (talk-over) no aplica esta regla y rige la lógica larga.
 * `lastUtterance` por defecto = `narration`.
 */
export function isTtsEcho(
  interim: string,
  narration: string,
  lastUtterance?: string,
  connectors?: readonly string[],
): boolean {
  const said = normalizeForEcho(interim);
  if (!said.length) return true;
  const last = normalizeForEcho(lastUtterance ?? narration);
  if (isEchoCommandPhrase(said)) return isTailOf(said, last);
  // Con `connectors` (descarte de transcript, isLikelyEcho): lo corto solo cae por cola de conector.
  // Sin `connectors` (talk-over: solo cuenta un barge-in, no descarta nada) rige la lógica larga.
  if (connectors && said.length <= ECHO_SHORT_MAX_TOKENS) {
    return isShortEchoOfConnector(said, connectors.map(normalizeForEcho));
  }
  const saidContent = contentTokens(said);
  if (saidContent.length < ECHO_MIN_CONTENT_TOKENS) return false;
  if (` ${last.join(' ')} `.includes(` ${said.join(' ')} `)) return true;
  const bag = new Set(contentTokens(normalizeForEcho(narration)));
  const overlap = saidContent.filter((t) => bag.has(t)).length;
  return overlap >= ECHO_MIN_CONTENT_TOKENS && overlap / saidContent.length >= TTS_ECHO_OVERLAP;
}

function clamp01(n: unknown): number | undefined {
  if (typeof n !== 'number' || !Number.isFinite(n)) return undefined;
  return Math.max(0, Math.min(1, n));
}

/**
 * Salvavidas (PLAN_LIFELINE.md §3.2) — traduce el snapshot del affect engine
 * al payload `affect_signals` del low-api. Señales ruidosas y solo de apoyo
 * (el BE les pone tope 0.25): cualquier campo no finito se omite, y sin
 * snapshot (engine apagado / no inicializado) no se manda nada — el BE
 * calcula el índice solo con las señales conversacionales.
 */
export function buildAffectSignals(
  snap: AffectSnapshot | null | undefined,
  ctx?: { transcript?: string; bargeInCount?: number; baselineWords?: number },
): FcLowAffectSignals | null {
  if (!snap) return null;
  const out: FcLowAffectSignals = {};
  const strain = clamp01(snap.axes?.strain);
  if (strain !== undefined) out.strain = strain;
  const hesitation = clamp01(snap.features?.hesitationDensity);
  if (hesitation !== undefined) out.hesitation_density = hesitation;
  const selfRepair = clamp01(snap.features?.selfRepairRate);
  if (selfRepair !== undefined) out.self_repair_rate = selfRepair;
  const ttfs = snap.features?.timeToFirstSpeechMs;
  if (typeof ttfs === 'number' && Number.isFinite(ttfs)) {
    // -1 = desconocido por contrato; cualquier otro negativo también lo es.
    out.time_to_first_speech_ms = ttfs >= 0 ? Math.round(ttfs) : -1;
  }
  if (typeof snap.features?.hasDistress === 'boolean') {
    out.has_distress = snap.features.hasDistress;
  }
  // Memoria de sesión del BE — telemetría del turno. Nada se adivina: sin
  // `snap.turn` (sin muestras de voz) se omiten duración/WPM/pausas.
  const turn = snap.turn;
  if (turn && Number.isFinite(turn.speechDurationMs) && turn.speechDurationMs >= 0) {
    const durMs = Math.round(turn.speechDurationMs);
    out.speech_duration_ms = durMs;
    // Solo palabras de la MISMA ventana que speech_duration_ms: si la ventana de
    // muestras se reabrió con el transcript retenido (followup aparcado), se
    // resta lo dicho antes del reset para no inflar el WPM.
    const baseline = Math.max(0, Math.floor(ctx?.baselineWords ?? 0));
    const words = Math.max(0, countWords(ctx?.transcript ?? '') - baseline);
    // ponytail: WPM sobre tiempo de cadencia (ver extractTurnTelemetry), no
    // mide el ASR; <1s es ruido y >600 es imposible (el BE rechaza) → omitir.
    if (durMs >= MIN_WPM_DURATION_MS && words > 0) {
      const wpm = Math.round(words / (durMs / 60000));
      if (wpm <= MAX_WPM) out.words_per_minute = wpm;
    }
    if (Number.isFinite(turn.longPauseCount) && turn.longPauseCount >= 0) {
      out.long_pause_count = Math.floor(turn.longPauseCount);
    }
  }
  // 0 = "no detectado" (el contador no distingue 0 real de ASR sin señal): solo >0.
  const barge = ctx?.bargeInCount;
  if (typeof barge === 'number' && Number.isFinite(barge) && barge > 0) {
    out.barge_in_count = Math.floor(barge);
  }
  return Object.keys(out).length > 0 ? out : null;
}

export interface FcLowRuntimeHost {
  /** Must call through FcSessionService.processCommand path (same as WS). */
  ingestCommand(command: AgentCommand): boolean;
  notifyPlaybackEnd(): void;
  setLocalAudioPlaying?(playing: boolean): void;
  /**
   * Optional — drive AgentState for shell chrome. Not used for greeting
   * name-capture (preparing would hide showCommandsHint); available for
   * post-question brain turns if the host wants preparing chrome.
   */
  setAgentState?(state: string): void;
  /** Current affect pace profile for fc-low adaptation_profile (optional). */
  getAdaptationProfile?(): string | null;
  /**
   * Salvavidas — snapshot del affect engine al cerrar el turno → se manda
   * como `affect_signals` (ver buildAffectSignals). Opcional: sin él (o con
   * el engine apagado) el submit_turn viaja igual que antes.
   */
  getAffectSnapshot?(): AffectSnapshot | null;
  /** Timer de respuesta del shell (solo lectura) para la priorización por dimensión del BE. */
  getAnswerTimer?(): FcLowAnswerTimer | null;
  /** True cuando NO se debe narrar una contrapregunta (gloria/overtime o
   *  quedan <=15s con el candidato aún hablando). */
  shouldSuppressFollowupNarration?(): boolean;
  /** Turn metrics (decision/settled latency, failures) → RUM. Optional: never required to run. */
  reportMetric?(name: string, value: number, unit?: 'ms' | 'count', extra?: Record<string, string | number | boolean>): void;
}

export interface FcLowRuntimeDeps {
  lowApi: Pick<FcLowApiService, 'boot' | 'startQuestions' | 'submitTurn'> &
    // Opcional: sin el, el runtime cae a submitTurn (buffered) y no hay gate de settled.
    Partial<Pick<FcLowApiService, 'submitTurnStream'>>;
  tts: Pick<FcBrowserTtsService, 'speak' | 'cancel'>;
  // STT optional for v1 — prefer transcript fed from ThinkingVoiceCommand via noteTranscript()
  /**
   * VC detection (CONTRATO-VC-DETECTION.md §2/ADENDA) — opcional a propósito:
   * quality_mode=high ya cubre su propio enforcement hablado server-side, y
   * los tests viejos de este runtime no lo pasan. Sin él, conferencingLadder
   * simplemente no arranca (no hay quién suscribirse) — no rompe nada.
   */
  vcScorer?: Pick<VcScorerService, 'state$' | 'state'>;
}

export interface FcLowRuntimeConfig {
  testId: string;
  email: string;
  sessionId: string;
}

function mapPhase(phase: FcLowPhase): string {
  switch (phase) {
    // Módulo de experiencia previa: el shell lo trata como el saludo (sin
    // timer visible, sin contador P1..Pn, sin card de pregunta, mic_open =
    // ventana nueva). El chip "Conociéndote · n/m" sale del campo `intro`.
    case 'greeting':
    case 'intro':
      return 'greeting';
    case 'followup':
      return 'followup';
    case 'farewell':
    case 'complete':
      return 'farewell';
    default:
      return 'question';
  }
}

async function asPromise<T>(value: Observable<T> | Promise<T>): Promise<T> {
  if (value && typeof (value as Observable<T>).subscribe === 'function') {
    return firstValueFrom(value as Observable<T>);
  }
  return value as Promise<T>;
}

/** reason del low-api cuando el BE rechaza un turno por idempotencia (expected_* no coincide). */
export const FC_LOW_STALE_TURN_REASON = 'stale_turn';
/** Línea local cuando el stale_turn es de la MISMA pregunta/prompt: pide repetir y reabre el mic. */
export const FC_LOW_STALE_REPEAT_LINE = 'Perdona, ¿me lo repites?';

/** Contexto derivado del prelude de applyResponse — lo necesita la etapa narrate. */
interface ApplyCtx {
  gen: number;
  assistantTurnId: string;
  thinkS: number;
  answerS: number;
  silenceS: number;
  narration: string;
  display: string;
  displayOrNarration: string;
  question: FcLowResponse['question'];
  advancedQuestion: boolean;
  looksLikeFollowup: boolean;
  isRephrase: boolean;
  isRepeat: boolean;
  isLifeline: boolean;
  lifelineText: string;
  lifelineFields: Record<string, unknown>;
  /** prompt_action del BE (o inferida por `reason` en BE viejo); null = sin dato. */
  promptAction: FcLowPromptAction | null;
}

export class FcLowRuntime {
  private stopped = false;
  private generation = 0;
  private config: FcLowRuntimeConfig | null = null;
  private latestTranscript = '';
  /** Segmentos FINALES ya confirmados de la respuesta en curso. Web Speech emite
   *  el habla larga en varios finales (reinicia la sesión tras cada pausa); si
   *  solo guardáramos el último, se perdería lo dicho antes. Se acumulan y se
   *  limpian al enviar el turno. */
  private finalizedTranscript = '';
  private lastNarrationText = '';
  private lastDisplayText = '';
  /** El último turno del BE fue un contexto: "repite" va al BE (vuelve a la pregunta), no se re-narra local. */
  private lastTurnWasContext = false;
  private lastTurnKind:
    | 'greeting'
    | 'question'
    | 'followup'
    | 'rephrase'
    | 'farewell' = 'question';
  /**
   * El último turno 'greeting' es en realidad un turno del módulo de
   * experiencia previa (phase 'intro'). Comparte el chrome del saludo, pero
   * su respuesta SÍ lleva conector y su repetición usa el display (sin el
   * "Mucho gusto…" del primer turno).
   */
  private lastTurnIntro = false;
  private lastIntroProgress: { turn: number; max: number } | null = null;
  private lastBudgets = { think_s: DEFAULT_THINK_S, answer_s: DEFAULT_ANSWER_S };
  private lastTurnSilenceS = DEFAULT_TURN_SILENCE_S;
  private lastTotalQuestions: number | undefined;
  private lastQuestionIndex: number | undefined;
  /** phase de la última respuesta APLICADA (prelude) — base para detectar stale_turn con estado avanzado. */
  private lastAppliedPhase: string | null = null;
  /** True tras narrar la 1ª pregunta — habilita el overlay de transición Q→Q. */
  private hasNarratedAQuestion = false;
  private lastAssistantTurnId: string | undefined;
  private submitting = false;
  private endOfSpeechGraceTimer: ReturnType<typeof setTimeout> | null = null;
  /** El último turno de Eva preguntó "¿quieres agregar algo más?" (active_prompt_kind confirm_advance). */
  private confirmAdvanceActive = false;
  /** Cierre del turno de confirmación cuando el candidato no habla. */
  private confirmNoSpeechTimer: ReturnType<typeof setTimeout> | null = null;
  /** Pausa concedida en el último turno de Eva: arranca al abrir su mic. */
  private grantedPauseS: number | null = null;
  private grantedSessionRemainingS: number | null = null;
  /** Pausa en curso: el ASR se ignora y no se cierra turno hasta retomar. */
  private pauseActive = false;
  private pauseResumeTimer: ReturnType<typeof setTimeout> | null = null;
  /** Absence ladder — only while question/followup answer window is open. */
  private absencePollTimer: ReturnType<typeof setInterval> | null = null;
  private absenceWatchEnabled = false;
  private answerWindowActive = false;
  private absenceStartedAtMs: number | null = null;
  private absenceCheckinsDone = 0;
  private lastVoicedAtMs: number | null = null;
  /** True while an absence check-in narration is in flight (blocks ladder ticks). */
  private absenceCheckinInFlight = false;
  /** True while browser TTS utterance is active. Once true, nothing interrupts. */
  private ttsActive = false;
  /** Nombre validado por el BE (`candidate_name` de la última respuesta). Única fuente. */
  private lastCandidateName: string | null = null;
  /** Pending filler gate timer while waiting on submitTurn. */
  private fillerGateTimer: ReturnType<typeof setTimeout> | null = null;
  /** True once the gate fired and filler TTS was kicked off. */
  private fillerStarted = false;
  /** Un conector local arrancó a hablarse en este submit (→ `ack_spoken` del request). */
  private ackSpoken = false;
  private serverAckMode = false;
  /** ASR local: candidato a mitad de elocución. */
  private midUtterance = false;
  /** Habla llegada después de mandar submit_turn y antes de narrar. */
  private speechDuringSubmit = false;
  /**
   * Enunciado abierto: se vacía cuando el candidato OYE a Eva, no cuando sale el POST.
   * `sentUtteranceText` es el snapshot del último POST. Lo que llegue después se
   * compara contra eso: migaja → se narra lo parkeado; crecimiento → revisión.
   */
  private utteranceId: string | null = null;
  private utteranceRevision = 0;
  private sentUtteranceText = '';
  /** Talk-over: veces que el candidato habló con Eva narrando (ciclo del turno). */
  private bargeInCount = 0;
  /** Ya se contó talk-over en la narración actual (1 por narración). */
  private bargeInThisTts = false;
  /** Texto de la narración de pregunta/followup en curso (null = TTS no elegible:
   *  ack, filler, farewell, check-in). Solo ahí se cuenta talk-over. */
  private ttsNarration: string | null = null;
  /**
   * Fuente única del filtro de eco: TODO texto que Eva dice por TTS local
   * (narración, ack, filler, check-ins, avisos, repeat). endedAt=null = sonando.
   */
  private recentSpeech: RecentSpeech[] = [];
  /** Entrada de recentSpeech del TTS que está sonando vía beginTts (para cerrarla en endTts). */
  private currentSpoken: RecentSpeech | null = null;
  /** Llegó habla NO-eco desde que arrancó el último TTS local. */
  private realSpeechSinceSpeech = false;
  /** Última actividad del ASR no-eco (interim/final/speech-active). Base del EOS. */
  private lastAsrActivityAt = 0;
  /** El recognizer del browser está reiniciando (onend → start): el EOS espera. */
  private recognizerRestarting = false;
  private recognizerRestartingSince = 0;
  /** Pista "colgado" del último noteTranscript (componente) — suma a looksIncompleteTranscript. */
  private eosIncompleteHint = false;
  /**
   * Idempotencia (BE): turn_seq / question.index de la ÚLTIMA respuesta que el BE
   * produjo y este runtime recibió (aplicada O parkeada: el estado del BE ya se
   * movió igual — si un comando descarta una respuesta parkeada, el siguiente
   * submit debe reflejar ese estado y no ser tomado por stale_turn).
   */
  private lastAppliedTurnSeq: number | undefined;
  private expectedQuestionIdx: number | undefined;
  /** turn_seq cuyo prelude ya corrió y cuya narración aún no terminó (parked/en curso). */
  private preludeAppliedForTurnSeq: number | null = null;
  /** Contexto del prelude aplicado — la continuación (narrate) se reanuda con esto. */
  /** Continuación parkeada en 'before_tts': el flush reanuda en narrate, nunca re-corre el prelude. */
  private parkedNarrateCtx: ApplyCtx | null = null;
  /** VAD marcó habla (onSpeechActive) durante la narración elegible actual. */
  private vadActiveThisTts = false;
  /** Último interim del ASR visto durante la narración elegible actual. */
  private talkOverInterim = '';
  /** Palabras de latestTranscript al abrir la ventana de muestras del affect
   *  engine (mic_open): el WPM solo cuenta lo dicho dentro de esa ventana. */
  private windowWordBase = 0;
  /** Respuesta del API retenida porque el humano no soltó el piso. */
  private parkedResponse: FcLowResponse | null = null;
  /** Promise del filler en curso, para dejarlo terminar antes de narrar. */
  private fillerSpeakPromise: Promise<void> | null = null;
  /**
   * Gate de apertura del mic (streaming por etapas): resuelve true cuando la
   * persistencia diferida del turno en curso termino bien (o tras el re-submit),
   * false si fallo (ya se emitio ERROR). Nunca rechaza. null = sin gate.
   */
  private settledGate: Promise<boolean> | null = null;
  /** El turno fallo post-decision (settled): el servicio ya esta en 'error'. Suprime
   *  assistant_logical_complete / mic_open / check-ins para no sacarlo de ese estado. */
  private turnFailed = false;
  private currentStream: FcLowTurnStream | null = null;
  /** True entre el POST del turno y la llegada de su decisión (Eva "pensando"). */
  private awaitingDecision = false;
  /**
   * El reloj de la pregunta venció (pass time_up) MIENTRAS Eva pensaba. El
   * submit en vuelo bloqueaba el pass y se perdía: la decisión (contrapregunta /
   * retry) se aplicaba con el tiempo ya vencido. Se retiene y se resuelve al
   * llegar la decisión (ver deferTimeUpDecision).
   */
  private pendingTimeUp = false;
  /**
   * El time-up retenido (pendingTimeUp) venció sobre un turno del módulo de
   * experiencia previa. Ahí NUNCA se manda pass: el candidato no pidió saltar
   * el módulo (el BE lo leía como "pasar" y saltaba directo a Q1).
   */
  private pendingTimeUpIntro = false;
  /** Últimos conectores dichos (más reciente al final): no se repiten los últimos 3. */
  private recentConnectors: string[] = [];
  /** Texto del conector/puente local en curso (null = ninguno). Lo usa noteTalkOver
   *  para no tratar el filler como narración elegible, aunque pase por beginTts(text). */
  private fillerPhrase: string | null = null;
  /** Caracteres de conectores encolados este turno (base del tope de espera). */
  private fillerSpokenChars = 0;
  /** Date.now() del primer conector del turno: la espera se ancla a su arranque. */
  private fillerStartedAt = 0;
  /** Se invalida en cada clearFillerGate: cancela puentes/limpiezas rezagadas. */
  private fillerEpoch = 0;
  /**
   * VC detection (CONTRATO-VC-DETECTION.md §2/§4.2/ADENDA) — conferencingLadder.
   * Contador de avisos ya dados: 0 = próximo es AVISO 1, 1 = próximo es AVISO 2,
   * >=maxStrikes = la próxima entrada a 'suspect' suspende directo. Persiste en
   * sessionStorage (key vc-strikes-fc:{testId}) — NO en memoria: un reload no
   * debe borrar los avisos ya dados (§0 "Avisos antes de cerrar: 2").
   */
  private vcStrikes = 0;
  /** True mientras se habla un AVISO 1/2 + corre su gracia — bloquea re-entrada. */
  private vcWarning = false;
  /** True una vez que se decidió suspender — evita doble-suspensión. */
  private vcSuspending = false;
  /** Cooldown tras un re-chequeo que limpió (score bajó): evita re-armar el
   *  mismo aviso en loop mientras el `level` del scorer (histéresis propia,
   *  120s) todavía no bajó de 'suspect' — ver recheckVcGrace(). */
  private vcQuietUntilMs = 0;
  private vcGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private vcSubscription: Subscription | null = null;

  constructor(
    private readonly host: FcLowRuntimeHost,
    private readonly deps: FcLowRuntimeDeps,
  ) {}

  async start(config: FcLowRuntimeConfig): Promise<void> {
    this.stopped = false;
    this.turnFailed = false;
    this.config = config;
    this.lastAppliedTurnSeq = undefined;
    this.expectedQuestionIdx = undefined;
    this.lastAppliedPhase = null;
    this.preludeAppliedForTurnSeq = null;
    this.parkedNarrateCtx = null;
    this.latestTranscript = '';
    this.windowWordBase = 0;
    this.finalizedTranscript = '';
    this.submitting = false;
    this.endTts();
    this.absenceCheckinInFlight = false;
    this.lastCandidateName = null;
    this.lastTurnIntro = false;
    this.lastIntroProgress = null;
    this.serverAckMode = false;
    this.generation++;
    this.clearAbsenceWatch();

    // VC detection — arranca la escalera de conferencing recién acá (no en el
    // constructor): necesita this.config.testId para la key de sessionStorage.
    this.vcWarning = false;
    this.vcSuspending = false;
    this.vcQuietUntilMs = 0;
    this.clearVcGraceTimer();
    this.loadVcStrikes();
    this.unsubscribeVcScorer();
    this.subscribeVcScorer();

    try {
      const res = await asPromise(
        this.deps.lowApi.boot(
          config.testId,
          config.email,
          config.sessionId,
          this.adaptationProfile(),
        ),
      );
      if (this.stopped) return;
      await this.applyResponse(res);
    } catch (err) {
      if (this.stopped) return;
      this.ingestError(err, 'No pudimos iniciar la entrevista.', false, 'boot');
    }
  }

  stop(): void {
    this.stopped = true;
    this.generation++;
    this.submitting = false;
    try {
      this.currentStream?.cancel?.();
    } catch {
      /* ignore */
    }
    this.currentStream = null;
    this.awaitingDecision = false;
    this.pendingTimeUp = false;
    this.pendingTimeUpIntro = false;
    this.endTts();
    this.parkedResponse = null;
    this.parkedNarrateCtx = null;
    this.preludeAppliedForTurnSeq = null;
    this.recentSpeech = [];
    this.currentSpoken = null;
    this.midUtterance = false;
    this.recognizerRestarting = false;
    this.speechDuringSubmit = false;
    this.bargeInCount = 0;
    this.bargeInThisTts = false;
    this.absenceCheckinInFlight = false;
    this.cancelEndOfSpeechGrace();
    this.cancelConfirmAdvanceNoSpeech();
    this.confirmAdvanceActive = false;
    this.cancelPauseResume();
    this.pauseActive = false;
    this.grantedPauseS = null;
    this.grantedSessionRemainingS = null;
    this.clearAbsenceWatch();
    this.clearFillerGate({ cancelTts: true });
    this.clearVcGraceTimer();
    this.unsubscribeVcScorer();
    try {
      this.deps.tts.cancel();
    } catch {
      /* ignore */
    }
  }

  /**
   * Intentionally a no-op: once Eva started speaking, nothing interrupts her.
   * Human last-word is enforced by not starting TTS while they still talk.
   */
  requestSoftBargeIn(): boolean {
    // Una vez que Eva arrancó a hablar, nada la corta (ni ruido, ni ASR).
    // El humano manda ANTES de que ella empiece (hold / no submit), no a
    // mitad de la locución.
    return false;
  }

  noteTranscript(text: string, opts?: { isFinal?: boolean; incomplete?: boolean }): void {
    // Quitar el comando de voz answer_now ("(eva) quiero responder/contestar")
    // que el ASR entrega MEZCLADO con la respuesta real: el FINAL del comando
    // puede llegar DESPUÉS de que thinking_ended limpió el buffer, re-poblándolo,
    // y sin este strip viaja al low-api como parte de la respuesta → Eva
    // contrapregunta sobre el comando. Solo strippea el comando al inicio.
    // Pausa en curso: lo que se diga no es respuesta ni comando.
    if (this.pauseActive) return;
    const trimmed = ThinkingVoiceCommandService.stripAnswerNowCommand(text || '');
    if (!trimmed) return;
    // Eco del propio TTS (filler/ack/narración): NO es habla del candidato — no
    // llena el buffer, no marca speechDuringSubmit, no cancela el EOS pendiente.
    if (this.isLikelyEcho(trimmed)) {
      console.log('[FC] asr_echo_dropped', { where: 'runtime', text: trimmed.slice(0, 80) });
      return;
    }
    this.realSpeechSinceSpeech = true;
    this.lastAsrActivityAt = Date.now();
    this.eosIncompleteHint = opts?.incomplete === true;
    // Any new transcript (interim or incomplete final) cancels a pending EOS grace.
    this.cancelEndOfSpeechGrace();
    if (this.submitting) this.speechDuringSubmit = true;
    this.talkOverInterim = trimmed;
    this.noteTalkOver();

    if (opts?.isFinal === true) {
      // Segmento cerrado: acumular (no sobrescribir) para no perder lo dicho en
      // segmentos previos cuando Web Speech reinicia la sesión tras una pausa.
      this.finalizedTranscript = this.finalizedTranscript
        ? `${this.finalizedTranscript} ${trimmed}`
        : trimmed;
      this.latestTranscript = this.finalizedTranscript;
    } else {
      // Interim: lo confirmado + lo que se está diciendo ahora (no se persiste).
      this.latestTranscript = this.finalizedTranscript
        ? `${this.finalizedTranscript} ${trimmed}`
        : trimmed;
    }
    // Mid-utterance / any ASR activity resets the absence ladder.
    this.markVoiced();

    if (this.submitting || this.stopped) return;
    // Interim o final, completo o colgado: el umbral lo decide eosThresholdMs()
    // (base adaptativa × factor si colgado), medido desde lastAsrActivityAt.
    this.armEndOfSpeechGrace();
  }

  /**
   * El recognizer del browser empezó (true) o terminó (false) de reiniciar. Mientras
   * reinicia no se cierra el turno por silencio: el hueco no es silencio del
   * candidato. Al completar, el silencio se mide desde el reinicio + umbral.
   */
  setRecognizerRestarting(restarting: boolean): void {
    if (restarting === this.recognizerRestarting) return;
    this.recognizerRestarting = restarting;
    if (restarting) {
      this.recognizerRestartingSince = Date.now();
      return;
    }
    if (this.stopped || this.submitting) return;
    if ((this.latestTranscript || '').trim()) {
      this.lastAsrActivityAt = Date.now();
      this.armEndOfSpeechGrace();
    }
  }

  private isRecognizerRestartingNow(): boolean {
    return (
      this.recognizerRestarting &&
      Date.now() - this.recognizerRestartingSince < FC_LOW_RECOGNIZER_RESTART_MAX_MS
    );
  }

  /** midUtterance false — belt+suspenders with final-complete path. */
  onSpeechSettled(): void {
    this.midUtterance = false;
    if (this.stopped || this.submitting) return;
    if (this.parkedResponse) {
      if (this.utteranceMateriallyGrew()) {
        // Habla nueva de verdad sobre un turno parkeado: esperar el silencio
        // y reenviar el enunciado completo. El primer hueco de ASR no alcanza.
        this.armEndOfSpeechGrace();
        return;
      }
      // Igual al snapshot, o migaja ("bueno", "por explicar"): narrar lo parkeado.
      this.restoreSentUtterance();
      this.speechDuringSubmit = false;
      void this.flushParkedResponse();
      return;
    }
    if (!(this.latestTranscript || '').trim()) return;
    // Siempre se (re)arma contra lastAsrActivityAt: idempotente. Antes un timer
    // ya existente hacía que este método no hiciera nada y el flag de mid del
    // componente (rezagado) provocaba un segundo re-armado (~7 s de cierre).
    this.armEndOfSpeechGrace();
  }

  /** midUtterance true — candidate still speaking; cancel pending EOS submit. */
  onSpeechActive(): void {
    this.midUtterance = true;
    // Con TTS local sonando (o en su cola de eco) y SIN habla no-eco confirmada
    // desde que arrancó, esto puede ser Eva oyéndose a sí misma: no cuenta como
    // "habló durante el submit" ni como actividad del ASR hasta que un transcript
    // no-eco (noteTranscript) lo confirme.
    const maybeEcho = this.localTtsAudible() && !this.realSpeechSinceSpeech;
    if (!maybeEcho) {
      this.lastAsrActivityAt = Date.now();
      if (this.submitting) this.speechDuringSubmit = true;
    }
    this.vadActiveThisTts = true;
    this.noteTalkOver();
    this.cancelEndOfSpeechGrace();
    this.markVoiced();
    // Sin respuesta retenida, el runtime sigue siendo dueño del cierre: re-arma
    // desde la actividad (no depende de que el componente emita "settled" en su
    // próximo tick de 1 s). Con una respuesta parkeada rige el flujo de hold.
    if (!this.parkedResponse && (this.latestTranscript || '').trim()) {
      this.armEndOfSpeechGrace();
    }
  }

  onClientQuestionTimer(
    phase: 'thinking_ended' | 'answer_budget_expired',
    _questionIdx: number | undefined,
    _reason?: 'timeout' | 'voice_command',
  ): void {
    if (this.stopped) return;
    // Thinking window only advances UI timers in the shell — do NOT submit.
    if (phase === 'thinking_ended') {
      // Descartar lo acumulado durante el PIENSE antes de abrir la respuesta: en
      // el piense el candidato no responde, así que todo texto del ASR ahí es el
      // comando de voz ("quiero responder") o ruido. Si no se limpia, ese residuo
      // se manda como respuesta al cerrar el turno y Eva contrapregunta (bug), o
      // ocupa un submit en vuelo que hace que un pass posterior se descarte.
      this.dropOpenUtterance();
      this.cancelEndOfSpeechGrace();
      this.enterAnswerWindow();
      return;
    }
    if (phase === 'answer_budget_expired') {
      this.cancelEndOfSpeechGrace();
      if (!this.parkedResponse && !(this.latestTranscript || '').trim()) return;
      void this.submitAccumulated('answer_budget');
    }
  }

  onClientCommand(command: 'pass' | 'repeat' | 'clarify' | 'context' | 'pause', opts?: { timeUp?: boolean }): void {
    if (this.stopped || this.pauseActive) return;
    this.cancelEndOfSpeechGrace();
    if (command === 'pass') {
      const endReason: FcLowEndReason = opts?.timeUp ? 'time_up' : 'user_command';
      if (opts?.timeUp && this.submitting && this.awaitingDecision) {
        // Eva está pensando: submitAccumulated lo descartaría (lock submitting).
        console.log('[FC] time_up_during_thinking — deferred until decision', {
          intro: this.lastTurnIntro,
        });
        this.pendingTimeUp = true;
        // lastTurnIntro aún describe el turno que se está respondiendo (se
        // actualiza recién al aplicar la decisión que viene en camino).
        this.pendingTimeUpIntro = this.lastTurnIntro;
        return;
      }
      // Corte duro por tiempo (overtime/gloria agotado) con habla real todavía
      // sin enviar: el candidato no pidió pasar, se quedó sin tiempo hablando.
      // Forzar PASS_TRANSCRIPT acá borraba esa respuesta real — quedaba en el
      // audio crudo pero nunca llegaba a Brain/evaluate — y la etiquetaba como
      // "el candidato pidió pasar la pregunta", falso (sesión de prueba
      // e0bd3c96: respuesta completa sobre CDN/cache-control perdida así).
      // Si hay transcript real acumulado, se manda ESE en vez del sentinel
      // (sin el hint de comando, para que se evalúe como respuesta real) —
      // pasarlo como `forced` también salta a propósito el guard de
      // `midUtterance` de submitAccumulated: este es el corte final, no hay
      // más tiempo de espera. El texto puede llegar incompleto a mitad de
      // frase; looksDanglingTranscript ya lo marca con [RESPUESTA_CORTADA]
      // aguas abajo, que es la señal correcta — no un pase explícito.
      const liveTranscript = (this.latestTranscript || '').trim();
      // Módulo de experiencia previa: el corte por tiempo (presupuesto de 90s
      // vencido) NO es un pase — el token de pase haría que el BE salte a Q1.
      // Se manda el turno tal cual (o '(silencio)' → el BE re-pregunta con
      // intro_repose). Solo "pasar"/"empecemos" dicho por el candidato salta.
      if (opts?.timeUp && this.lastTurnIntro) {
        void this.submitAccumulated('answer_budget', liveTranscript || EMPTY_ANSWER_FALLBACK);
        return;
      }
      if (opts?.timeUp && liveTranscript) {
        void this.submitAccumulated(endReason, liveTranscript);
        return;
      }
      void this.submitAccumulated(endReason, PASS_TRANSCRIPT, buildDetectedCommandHint('pass'));
      return;
    }
    if (command === 'clarify') {
      void this.submitAccumulated('user_command', CLARIFY_TRANSCRIPT, buildDetectedCommandHint('clarify'));
      return;
    }
    if (command === 'context') {
      void this.submitAccumulated('user_command', CONTEXT_TRANSCRIPT, buildDetectedCommandHint('context'));
      return;
    }
    if (command === 'pause') {
      void this.submitAccumulated('user_command', PAUSE_TRANSCRIPT);
      return;
    }
    // repeat — full re-narration cycle (turn_started + TTS + mic_open), no fc-low call.
    // Clear any command-phrase transcript so a later EOS/budget does not submit it.
    this.latestTranscript = '';
    this.windowWordBase = 0;
    // Tras un contexto, lo que hay en pantalla ES el contexto: re-narrarlo local repetiría
    // el contexto. El BE devuelve la pregunta y la reescribe en la caja (voz = caja).
    if (this.lastNarrationText && !this.lastTurnWasContext) {
      void this.repeatLastTurn();
      return;
    }
    void this.submitAccumulated('user_command', 'repite la pregunta', buildDetectedCommandHint('repeat'));
  }

  /** Silencio base ADAPTATIVO en ms: turn_silence_s del último mic_open, acotado a la escalera. */
  private baseSilenceMs(): number {
    const ms = Math.round((this.lastTurnSilenceS || DEFAULT_TURN_SILENCE_S) * 1000);
    return Math.min(EOS_MAX_MS, Math.max(EOS_MIN_MS, ms));
  }

  /**
   * Umbral de cierre por silencio para `text`: base adaptativa si la frase
   * cerró, base × EOS_INCOMPLETE_FACTOR si quedó colgada (acotado a la escalera).
   * Público: el componente deriva de acá su gracia de mid-utterance.
   */
  getEosThresholdMs(text?: string): number {
    const t = text ?? this.latestTranscript;
    const hung = this.eosIncompleteHint || looksIncompleteTranscript(t);
    const ms = hung ? Math.round(this.baseSilenceMs() * EOS_INCOMPLETE_FACTOR) : this.baseSilenceMs();
    return Math.min(EOS_MAX_MS, Math.max(EOS_MIN_MS, ms));
  }

  // -------------------------------------------------------------------------
  // Filtro de eco del TTS local (fuente única de verdad)
  // -------------------------------------------------------------------------

  /** Registra un texto hablado por TTS local. Eva habla de a una voz: cierra las abiertas. */
  private recordSpoken(text: string, connector = false): RecentSpeech {
    const now = Date.now();
    for (const e of this.recentSpeech) if (e.endedAt == null) e.endedAt = now;
    const entry: RecentSpeech = { text, endedAt: null, connector };
    this.recentSpeech.push(entry);
    if (this.recentSpeech.length > ECHO_MAX_ENTRIES) this.recentSpeech.shift();
    this.realSpeechSinceSpeech = false;
    return entry;
  }

  /** ¿Eva está hablando (TTS local) o dentro de la cola de eco tras terminar? */
  private localTtsAudible(): boolean {
    if (this.ttsActive) return true;
    const now = Date.now();
    return this.recentSpeech.some(
      (e) => e.endedAt == null || now - e.endedAt <= FC_LOW_ECHO_TAIL_MS,
    );
  }

  /**
   * ¿`text` (ASR del browser) es el propio TTS de Eva? Solo si Eva habla o está
   * en su cola de eco (~1.5 s) Y el texto solapa (≥ TTS_ECHO_OVERLAP de tokens,
   * o es subcadena — cubre ecos cortos como "sin problema") con lo que dijo en
   * los últimos ~8 s (filler, ack, narración, check-ins, avisos, repeat).
   */
  isLikelyEcho(text: string): boolean {
    if (!(text || '').trim() || !this.localTtsAudible()) return false;
    const now = Date.now();
    const recent = this.recentSpeech.filter(
      (e) => e.endedAt == null || now - e.endedAt <= FC_LOW_ECHO_RECENT_MS,
    );
    const spoken = recent.map((e) => e.text).join(' ');
    if (!spoken.trim()) return false;
    // Cola literal: solo cuenta la ÚLTIMA locución y solo mientras suena / en su cola de eco.
    const lastEntry = this.recentSpeech[this.recentSpeech.length - 1];
    const lastInTail =
      !!lastEntry && (lastEntry.endedAt == null || now - lastEntry.endedAt <= FC_LOW_ECHO_TAIL_MS);
    const connectors = recent.filter((e) => e.connector).map((e) => e.text);
    return isTtsEcho(text, spoken, lastInTail || !lastEntry ? lastEntry?.text : '', connectors);
  }

  /** Timer de respuesta del shell; un fallo del host no corta el turno. */
  private answerTimer(): FcLowAnswerTimer | null {
    try {
      return this.host.getAnswerTimer?.() ?? null;
    } catch {
      return null;
    }
  }

  private adaptationProfile(): string | null {
    try {
      return this.host.getAdaptationProfile?.() ?? null;
    } catch {
      return null;
    }
  }

  /** Salvavidas — affect_signals del turno que se cierra (null = no se manda). */
  private affectSignals(transcript: string): FcLowAffectSignals | null {
    try {
      return buildAffectSignals(this.host.getAffectSnapshot?.() ?? null, {
        transcript,
        bargeInCount: this.bargeInCount,
        baselineWords: this.windowWordBase,
      });
    } catch {
      return null;
    }
  }

  /** Primer nombre validado por el BE ('' = sin nombre: nunca se inventa ni se extrae en el FE). */
  private candidateFirstName(): string {
    return (this.lastCandidateName || '').split(/\s+/).filter(Boolean)[0] ?? '';
  }

  /** think_s for mic_open after ack / barge-in — 0 when answer window already open. */
  private micThinkSForResume(): number {
    if (
      this.answerWindowActive ||
      this.lastTurnKind === 'followup' ||
      this.lastTurnKind === 'rephrase'
    ) {
      return 0;
    }
    return this.lastBudgets.think_s;
  }

  /**
   * Arma el cierre por silencio: dispara cuando now − lastAsrActivityAt ≥
   * getEosThresholdMs(). Al vencer el timer se RE-CHEQUEA contra la actividad
   * (no contra el flag midUtterance del componente, que es rezagado: expira a
   * la par de este mismo umbral y re-armaba otra espera completa).
   */
  private armEndOfSpeechGrace(): void {
    this.cancelEndOfSpeechGrace();
    if (this.stopped || this.submitting) return;
    if (!(this.latestTranscript || '').trim()) return;
    const remaining = Math.max(
      this.isRecognizerRestartingNow() ? RECOGNIZER_RESTART_POLL_MS : 0,
      this.getEosThresholdMs() - (Date.now() - this.lastAsrActivityAt),
    );
    this.endOfSpeechGraceTimer = setTimeout(() => {
      this.endOfSpeechGraceTimer = null;
      if (this.stopped || this.submitting) return;
      if (this.isRecognizerRestartingNow()) {
        this.armEndOfSpeechGrace(); // el hueco del recognizer no es silencio del candidato
        return;
      }
      const stillNeeded = this.getEosThresholdMs() - (Date.now() - this.lastAsrActivityAt);
      if (stillNeeded > 0) {
        this.armEndOfSpeechGrace();
        return;
      }
      void this.submitAccumulated('eos_grace');
    }, remaining);
  }

  private cancelEndOfSpeechGrace(): void {
    if (this.endOfSpeechGraceTimer != null) {
      clearTimeout(this.endOfSpeechGraceTimer);
      this.endOfSpeechGraceTimer = null;
    }
  }

  /** Marca del mic_open de la confirmación de avance: el shell congela el timer de la pregunta. */
  private confirmAdvanceFlag(): { confirm_advance?: true } {
    if (!this.confirmAdvanceActive) return {};
    return { confirm_advance: true };
  }

  /**
   * Turno de confirmación sin habla: a los turn_silence_s se cierra con '(silencio)'
   * y el BE ejecuta el avance retenido. Si el candidato habla, el EOS normal manda lo dicho.
   */
  private armConfirmAdvanceNoSpeech(): void {
    this.cancelConfirmAdvanceNoSpeech();
    if (!this.confirmAdvanceActive || this.stopped) return;
    const armedAt = Date.now();
    this.confirmNoSpeechTimer = setTimeout(() => {
      this.confirmNoSpeechTimer = null;
      if (this.stopped || this.submitting || !this.confirmAdvanceActive) return;
      if ((this.latestTranscript || '').trim()) return;
      if (this.lastAsrActivityAt > armedAt) {
        this.armConfirmAdvanceNoSpeech();
        return;
      }
      void this.submitAccumulated('eos_grace', EMPTY_ANSWER_FALLBACK);
    }, this.baseSilenceMs());
  }

  /**
   * Pausa concedida: se descarta lo dicho y el ASR se ignora hasta que el candidato
   * da Continuar (resumePause). Si la prueba se agota antes (pause_s congelados +
   * session_remaining_s), se pide retomar solo y el BE cierra por tiempo.
   * Devuelve la marca del mic_open (el shell congela el timer y abre el modal).
   */
  private beginGrantedPause(): { pause_s?: number } {
    const pauseS = this.grantedPauseS;
    const remainingS = this.grantedSessionRemainingS;
    this.grantedPauseS = null;
    this.grantedSessionRemainingS = null;
    if (pauseS === null || this.stopped) return {};
    this.pauseActive = true;
    this.dropOpenUtterance();
    this.cancelEndOfSpeechGrace();
    this.cancelPauseResume();
    if (remainingS !== null) {
      this.pauseResumeTimer = setTimeout(() => this.resumePause(), (pauseS + remainingS) * 1000);
    }
    return { pause_s: pauseS };
  }

  /** Continuar del modal de pausa: el BE resume dónde quedaron y repite el enunciado. */
  resumePause(): void {
    if (!this.pauseActive || this.stopped) return;
    this.cancelPauseResume();
    this.pauseActive = false;
    void this.submitAccumulated('user_command', PAUSE_RESUME_TRANSCRIPT);
  }

  private cancelPauseResume(): void {
    if (this.pauseResumeTimer == null) return;
    clearTimeout(this.pauseResumeTimer);
    this.pauseResumeTimer = null;
  }

  private cancelConfirmAdvanceNoSpeech(): void {
    if (this.confirmNoSpeechTimer != null) {
      clearTimeout(this.confirmNoSpeechTimer);
      this.confirmNoSpeechTimer = null;
    }
  }

  private clearAbsenceWatch(): void {
    if (this.absencePollTimer != null) {
      clearInterval(this.absencePollTimer);
      this.absencePollTimer = null;
    }
    this.absenceWatchEnabled = false;
    this.answerWindowActive = false;
    this.absenceStartedAtMs = null;
    this.absenceCheckinsDone = 0;
    this.lastVoicedAtMs = null;
    this.absenceCheckinInFlight = false;
  }

  /**
   * Start absence polling after mic_open on question/followup (never greeting).
   * Answer-window clock starts immediately when think_s=0 (followup); otherwise
   * waits for thinking_ended.
   */
  private startAbsenceWatch(answerImmediate: boolean): void {
    this.clearAbsenceWatch();
    if (this.stopped) return;
    if (!answerImmediate) {
      // Arranca un piense: el residuo del buffer (p.ej. un final repetido de la
      // respuesta anterior) no es respuesta a esta pregunta. Sin esto, un re-armado
      // del EOS (speech_state, reinicio del recognizer) lo mandaba en pleno piense.
      this.dropOpenUtterance();
      this.cancelEndOfSpeechGrace();
    }
    this.absenceWatchEnabled = true;
    this.answerWindowActive = answerImmediate;
    this.absenceStartedAtMs = answerImmediate ? Date.now() : null;
    this.absencePollTimer = setInterval(() => this.tickAbsence(), ABSENCE_POLL_MS);
  }

  private enterAnswerWindow(): void {
    if (!this.absenceWatchEnabled || this.stopped) return;
    this.answerWindowActive = true;
    this.absenceStartedAtMs = Date.now();
    this.absenceCheckinsDone = 0;
    this.lastVoicedAtMs = null;
  }

  private markVoiced(): void {
    if (!this.absenceWatchEnabled) return;
    this.lastVoicedAtMs = Date.now();
    // Keep clock fresh while voiced; check-ins drop (Python voiced_recent reset).
    if (this.answerWindowActive) {
      this.absenceStartedAtMs = Date.now();
      this.absenceCheckinsDone = 0;
    }
  }

  private tickAbsence(): void {
    if (
      this.stopped ||
      this.pauseActive ||
      this.submitting ||
      this.absenceCheckinInFlight ||
      this.ttsActive ||
      !this.absenceWatchEnabled ||
      !this.answerWindowActive
    ) {
      return;
    }

    const now = Date.now();
    const voicedRecent =
      this.lastVoicedAtMs != null &&
      now - this.lastVoicedAtMs < ABSENCE_VOICED_RESET_S * 1000;

    if (voicedRecent) {
      this.absenceStartedAtMs = now;
      this.absenceCheckinsDone = 0;
      return;
    }

    if (this.absenceStartedAtMs == null) {
      this.absenceStartedAtMs = now;
      return;
    }

    const absentForS = (now - this.absenceStartedAtMs) / 1000;
    const action = absenceNextAction(absentForS, this.absenceCheckinsDone);

    if (action === 'checkin') {
      if (this.absenceCheckinInFlight || this.ttsActive) return;
      this.absenceCheckinsDone += 1;
      void this.speakAbsenceCheckin();
      return;
    }

    if (action === 'finalize') {
      this.clearAbsenceWatch();
      void this.submitAccumulated('absence_timeout', ABSENT_TRANSCRIPT);
    }
  }

  /**
   * Absence check-in through the same narration chrome as high `_narrate(..., kind="ack")`:
   * assistant_turn_started(ack) → local-audio-playing → TTS → logical_complete →
   * playback end → mic_open. Does NOT reset the absence ladder.
   */
  private async speakAbsenceCheckin(): Promise<void> {
    if (this.stopped || this.turnFailed) return;
    this.absenceCheckinInFlight = true;

    const name = this.candidateFirstName();
    const text = name ? `${name}, ¿estás ahí?` : '¿Sigues ahí?';
    const assistantTurnId = `low-ack-${++this.generation}`;
    const gen = this.generation;
    this.lastAssistantTurnId = assistantTurnId;

    this.host.ingestCommand({
      type: 'assistant_turn_started',
      text: '',
      display_text: text,
      phase: 'ack',
      turn_kind: 'ack',
      questionIndex: this.lastQuestionIndex,
      assistant_turn_id: assistantTurnId,
    });

    this.host.setLocalAudioPlaying?.(true);
    this.beginTts(text, false); // check-in: no elegible a talk-over, sí al filtro de eco
    try {
      await this.deps.tts.speak(text);
    } catch {
      /* non-fatal */
    }
    this.endTts();

    if (this.stopped || gen !== this.generation) {
      // Soft barge-in already reopened mic / cleared playing.
      this.host.setLocalAudioPlaying?.(false);
      this.absenceCheckinInFlight = false;
      return;
    }

    this.host.ingestCommand({
      type: 'assistant_logical_complete',
      assistant_turn_id: assistantTurnId,
      requires_playback_ack: false,
      questionIndex: this.lastQuestionIndex,
    });

    this.host.notifyPlaybackEnd();
    this.host.setLocalAudioPlaying?.(false);

    this.markAnswerWindowStart();
    this.host.ingestCommand({
      type: 'mic_open',
      thinking_time_s: this.micThinkSForResume(),
      answer_budget_s: this.lastBudgets.answer_s,
      turn_silence_s: this.lastTurnSilenceS,
      ...this.confirmAdvanceFlag(),
      questionIndex: this.lastQuestionIndex,
      assistant_turn_id: assistantTurnId,
    });

    this.absenceCheckinInFlight = false;
  }

  /**
   * Full re-narration like high: assistant_turn_started → TTS → logical_complete
   * → playback end → mic_open. Never emits next_question.
   */
  private async repeatLastTurn(): Promise<void> {
    // Repetir lo que está en pantalla: el narration_text del BE puede traer
    // puente/ayuda/"Sigue, te escucho" solo-voz que no debe re-decirse.
    // El saludo no tiene display (se usa la narración).
    const narration =
      ((this.lastTurnKind !== 'greeting' || this.lastTurnIntro) && this.lastDisplayText) ||
      this.lastNarrationText;
    if (!narration || this.stopped || this.turnFailed) return;

    // New narration cycle — pause absence until mic_open again.
    this.clearAbsenceWatch();

    const gen = ++this.generation;
    const turnKind = this.lastTurnKind === 'greeting' ? 'greeting' : this.lastTurnKind;
    const assistantTurnId = `low-repeat-${gen}`;
    this.lastAssistantTurnId = assistantTurnId;

    const startedText =
      turnKind === 'greeting' ? '' : this.lastDisplayText || narration;
    const introField =
      turnKind === 'greeting' && this.lastTurnIntro && this.lastIntroProgress
        ? { intro: this.lastIntroProgress }
        : {};

    this.host.ingestCommand({
      type: 'assistant_turn_started',
      text: startedText,
      display_text: narration,
      phase: turnKind,
      turn_kind: turnKind,
      renarration: true,
      ...introField,
      questionIndex: this.lastQuestionIndex,
      assistant_turn_id: assistantTurnId,
    });

    this.host.setLocalAudioPlaying?.(true);
    this.beginTts(narration);
    try {
      await this.deps.tts.speak(narration);
    } catch {
      /* non-fatal */
    }
    this.endTts();

    if (this.stopped || gen !== this.generation) {
      this.host.setLocalAudioPlaying?.(false);
      return;
    }

    this.host.ingestCommand({
      type: 'assistant_logical_complete',
      assistant_turn_id: assistantTurnId,
      requires_playback_ack: false,
      questionIndex: this.lastQuestionIndex,
    });

    this.host.notifyPlaybackEnd();
    this.host.setLocalAudioPlaying?.(false);

    const thinkS =
      this.lastTurnKind === 'followup' || this.lastTurnKind === 'rephrase'
        ? 0
        : this.lastBudgets.think_s;
    if (!(await this.micGateOpen(gen))) return;
    this.markAnswerWindowStart();
    this.host.ingestCommand({
      type: 'mic_open',
      thinking_time_s: thinkS,
      answer_budget_s: this.lastBudgets.answer_s,
      turn_silence_s: this.lastTurnSilenceS,
      ...this.confirmAdvanceFlag(),
      questionIndex: this.lastQuestionIndex,
      assistant_turn_id: assistantTurnId,
    });

    if (
      this.lastTurnKind === 'question' ||
      this.lastTurnKind === 'followup' ||
      this.lastTurnKind === 'rephrase'
    ) {
      this.startAbsenceWatch(
        thinkS <= 0 ||
          this.lastTurnKind === 'followup' ||
          this.lastTurnKind === 'rephrase',
      );
    }
  }

  private clearFillerGate(opts?: { cancelTts?: boolean }): void {
    if (this.fillerGateTimer != null) {
      clearTimeout(this.fillerGateTimer);
      this.fillerGateTimer = null;
    }
    this.fillerEpoch++;
    if (opts?.cancelTts && this.fillerStarted) {
      try {
        this.deps.tts.cancel();
      } catch {
        /* ignore */
      }
      this.endFillerTts();
    }
    this.fillerStarted = false;
    this.fillerSpeakPromise = null;
    this.fillerSpokenChars = 0;
    this.fillerStartedAt = 0;
  }

  /** Cierra el flag de TTS del conector (solo si sigue siendo el TTS vigente). */
  private endFillerTts(): void {
    if (this.fillerPhrase !== null && this.ttsNarration === this.fillerPhrase) {
      this.endTts();
      this.ttsNarration = null;
    }
    this.fillerPhrase = null;
  }

  /** Habla un conector local. SIEMPRE vía beginTts(text) (filtro de eco). */
  private speakFiller(phrase: string, onStart?: () => void): Promise<void> {
    if (!this.fillerStarted) this.fillerStartedAt = Date.now();
    this.fillerStarted = true;
    this.fillerSpokenChars += phrase.length;
    this.beginTts(phrase, true, true);
    onStart?.();
    this.fillerPhrase = phrase;
    return Promise.resolve(this.deps.tts.speak(phrase))
      .catch(() => {
        /* filler TTS errors are non-fatal */
      })
      .then(() => {
        if (this.fillerPhrase === phrase) this.endFillerTts();
      });
  }

  private newUtteranceId(): string {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) return uuid;
    return `utt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /** Texto del buffer que no estaba en el último POST. Sin snapshot, no hay delta. */
  private unsentDelta(): string {
    const current = (this.latestTranscript || '').trim();
    const sent = this.sentUtteranceText.trim();
    if (!current || !sent || current === sent) return '';
    if (current.startsWith(sent)) return current.slice(sent.length).trim();
    const at = current.indexOf(sent);
    if (at >= 0) {
      return `${current.slice(0, at)} ${current.slice(at + sent.length)}`.replace(/\s+/g, ' ').trim();
    }
    return current;
  }

  /** Un comando corto no es migaja: hay que mandarlo, aunque sean una o dos palabras. */
  private deltaIsShortCommand(delta: string): boolean {
    const n = delta
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!n || countWords(n) > 6) return false;
    return (
      /^(pasa|pasar|siguiente|repito|repiteme|repite|no entendi|aclarame|aclaramela)$/.test(n) ||
      /^(quiero pasar|pasa a la siguiente|siguiente pregunta)$/.test(n)
    );
  }

  /**
   * El candidato agregó habla nueva de verdad respecto del POST en vuelo.
   * Menos de 3 palabras y sin comando ("bueno", "por explicar") es migaja.
   */
  private utteranceMateriallyGrew(): boolean {
    if (!this.sentUtteranceText.trim()) return false;
    const delta = this.unsentDelta();
    if (!delta) return false;
    if (this.deltaIsShortCommand(delta)) return true;
    return countWords(delta) >= 3;
  }

  /** Saca la migaja del buffer para que no se cuele en el próximo turno. */
  private restoreSentUtterance(): void {
    const sent = this.sentUtteranceText.trim();
    if (!sent) return;
    this.latestTranscript = sent;
    this.finalizedTranscript = sent;
  }

  /**
   * El candidato va a oír a Eva. Se cierra el enunciado. Si quedó habla nueva
   * (pista narrada primero), esa cola queda como buffer de un enunciado nuevo.
   */
  private sealHeardUtterance(): void {
    const delta = this.unsentDelta();
    this.utteranceId = null;
    this.utteranceRevision = 0;
    this.sentUtteranceText = '';
    this.latestTranscript = delta;
    this.finalizedTranscript = delta;
    this.windowWordBase = 0;
  }

  /** Comando, sentinela o avance: este buffer no revisa la respuesta ya mandada. */
  private dropOpenUtterance(): void {
    this.utteranceId = null;
    this.utteranceRevision = 0;
    this.sentUtteranceText = '';
    this.latestTranscript = '';
    this.finalizedTranscript = '';
    this.windowWordBase = 0;
  }

  private humanHoldsFloor(): boolean {
    // El texto YA enviado no es "sigue hablando". Si lo fuera, conservar el
    // buffer parkeaba todas las respuestas y el flush no narraba nunca.
    return this.midUtterance || this.speechDuringSubmit || this.utteranceMateriallyGrew();
  }

  /**
   * If the human still holds the floor, do not start Eva — including
   * next_question. Question-start takeover only after they actually settled.
   * Greeting/ack is the exception (briefing).
   */
  private shouldParkForHumanFloor(res: FcLowResponse): boolean {
    // Un flush deliberado (el candidato YA se asentó y decidimos narrar lo
    // retenido) nunca se re-parkea — sin esto, el buffer conservado del path
    // followup mantenía humanHoldsFloor() en true y el flush loopeaba parked
    // → park → parked sin narrar jamás.
    if (this.flushingParked) return false;
    if (!this.humanHoldsFloor()) return false;
    // El módulo de experiencia previa (phase 'intro' / reason 'intro_complete')
    // es una continuación del saludo: misma excepción.
    const isGreeting =
      res.phase === 'greeting' ||
      res.phase === 'intro' ||
      res.reason === 'greeting_complete' ||
      res.reason === INTRO_COMPLETE_REASON;
    return !isGreeting;
  }

  private flushingParked = false;

  private async flushParkedResponse(): Promise<void> {
    const parked = this.parkedResponse;
    if (!parked || this.stopped) return;
    this.parkedResponse = null;
    // Continuación (prelude ya aplicado) → se reanuda en narrate; sin ella, se
    // aplica desde cero (la respuesta se parkeó antes de cualquier efecto).
    const resumeCtx = this.parkedNarrateCtx;
    this.parkedNarrateCtx = null;
    this.speechDuringSubmit = false;
    this.midUtterance = false;
    this.flushingParked = true;
    // Bugs dev 2026-09-30 (#2 ack cortado / #3 ack doble): el avance narrado
    // por este camino corría SIN el lock `submitting` — durante el ack y los
    // 2200ms de transición (localAudioPlaying=false) un final tardío del ASR
    // armaba la grace y disparaba OTRO submitAccumulated, cuyo applyResponse
    // cortaba el ack a mitad (el speak() nuevo cancela el TTS en curso) o
    // narraba un segundo ack. Sostener el lock deja este camino tan protegido
    // como el submit inline (que mantiene submitting=true durante TODO su
    // applyResponse).
    this.submitting = true;
    try {
      await this.applyResponse(parked, resumeCtx ?? undefined);
    } finally {
      this.flushingParked = false;
      this.submitting = false;
    }
  }

  /**
   * True si la respuesta parkeada sirve una pista de salvavidas. Consume cupo
   * escaso en el BE y no tiene rollback (unheard_turn_seq revierte prompts, no
   * cupo): NUNCA se descarta, se narra primero aunque haya habla nueva.
   */
  private parkedServesLifeline(res: FcLowResponse): boolean {
    return res.reason === LIFELINE_HINT_REASON || res.lifeline?.served_this_turn === true;
  }

  /**
   * True si la respuesta parkeada AVANZA la entrevista (pregunta nueva /
   * farewell / complete) — el candidato aún no la escuchó, así que lo que diga
   * mientras tanto son repeticiones del comando o talk-over, nunca una
   * respuesta a la pregunta nueva. Distingue el manejo del buffer en el guard
   * de submitAccumulated (ver espiral de avances, logs3).
   */
  private parkedIsAdvance(res: FcLowResponse): boolean {
    // Continuación: el prelude ya movió lastQuestionIndex, así que la comparación
    // de índices de abajo ya no ve el avance — lo dice el contexto guardado.
    if (this.parkedNarrateCtx?.advancedQuestion) return true;
    return (
      (!!res.question &&
        typeof res.question.index === 'number' &&
        res.question.index !== this.lastQuestionIndex) ||
      res.phase === 'farewell' ||
      res.phase === 'complete' ||
      res.interview_complete === true ||
      !!(res.advance_ack || '').trim()
    );
  }

  /**
   * CONTRATO: todo TTS local pasa por acá CON su texto (filler, ack, narración,
   * check-ins, avisos, repeat) — es la entrada del filtro de eco (recentSpeech).
   * `talkOverEligible=false` (ack, check-in, aviso, farewell, bridge) deja el TTS
   * fuera del conteo de talk-over pero igual registrado para el filtro de eco.
   */
  private beginTts(text: string, talkOverEligible = true, connector = false): void {
    this.ttsActive = true;
    this.bargeInThisTts = false;
    this.ttsNarration = talkOverEligible && text?.trim() ? text : null;
    this.vadActiveThisTts = false;
    this.talkOverInterim = '';
    if ((text || '').trim()) this.currentSpoken = this.recordSpoken(text, connector);
  }

  /** Fin del TTS local: cierra la entrada de eco (arranca la cola de ~1.5 s). */
  private endTts(): void {
    this.ttsActive = false;
    if (this.currentSpoken) {
      if (this.currentSpoken.endedAt == null) this.currentSpoken.endedAt = Date.now();
      this.currentSpoken = null;
    }
  }

  /** Nueva ventana de muestras del affect engine (mic_open): el WPM parte de acá. */
  private markAnswerWindowStart(): void {
    this.windowWordBase = countWords(this.latestTranscript);
    this.armConfirmAdvanceNoSpeech();
  }

  /**
   * Candidato hablando con Eva narrando → +1 por narración (no por interim).
   * ponytail: techo = el ASR del navegador puede oír el eco del TTS; Eva no
   * se corta (requestSoftBargeIn es no-op), así que es talk-over, no corte.
   */
  private noteTalkOver(): void {
    // Solo narración de pregunta/followup (no filler/ack/farewell) y con
    // evidencia de habla real: VAD activo + interim que NO es eco del TTS.
    if (!this.ttsActive || this.ttsNarration === null || this.bargeInThisTts) return;
    if (this.ttsNarration === this.fillerPhrase) return; // conector local: no elegible
    if (!this.vadActiveThisTts || !this.talkOverInterim) return;
    if (isTtsEcho(this.talkOverInterim, this.ttsNarration)) return;
    this.bargeInThisTts = true;
    this.bargeInCount++;
  }

  /** Tipo de conector según cómo se cerró el turno (null = sin conector). */
  private connectorKindFor(
    transcript: string,
    hint?: FcLowDetectedCommandHint,
    fromGreeting?: boolean,
  ): ConnectorKind | null {
    // Greeting: el FE NO habla acuse local; el BE dice "Mucho gusto, {nombre}." con su nombre validado.
    if (fromGreeting) return null;
    switch (hint?.command) {
      case 'pass':
        return 'command_pass';
      case 'clarify':
      case 'context':
        return 'command_clarify';
      case 'repeat':
        return 'command_repeat';
      case 'end':
        return null;
    }
    // Sentinels internos ((silencio), (comando:absent)...): nada que acusar.
    if (transcript.startsWith('(')) return null;
    return 'content';
  }

  /**
   * Arma el conector hablado mientras se espera al brain. Con
   * FC_LOW_FILLER_GATE_MS = 0 se dispara en el MISMO tick del submit (percepción
   * de respuesta inmediata). Elige el conector por largo (contenido/larga) o por
   * comando detectado (pass/clarify/repeat) y rota sin repetir los últimos 3.
   * Respuestas cortas (<8 palabras) y greeting: sin conector (pickConnector null).
   * Máximo UN acuse por turno: no hay segundo puente. Marca `ackSpoken` solo
   * cuando el TTS del conector ARRANCÓ.
   */
  private scheduleFillerGate(
    transcript: string,
    hint?: FcLowDetectedCommandHint,
    fromGreeting = false,
    endReason?: FcLowEndReason,
  ): void {
    this.clearFillerGate({ cancelTts: false });
    if (endReason === 'time_up') return;
    // eos_grace ya verificó el silencio: el flag midUtterance del componente es
    // rezagado (stale-true) y mataba TODOS los conectores. Solo cuenta habla
    // real nueva (speechDuringSubmit).
    const ignoreMid = endReason === 'eos_grace';
    const holdsFloor = () => (!ignoreMid && this.midUtterance) || this.speechDuringSubmit;
    if (holdsFloor()) return;
    const kind = this.connectorKindFor(transcript, hint, fromGreeting);
    if (!kind) return;
    const fire = () => {
      this.fillerGateTimer = null;
      if (this.stopped || !this.submitting) return;
      if (holdsFloor()) return;
      const phrase = pickConnector(transcript, kind, {
        recent: this.recentConnectors,
      });
      if (phrase) {
        this.recentConnectors = [...this.recentConnectors, phrase].slice(-FC_LOW_CONNECTOR_NO_REPEAT);
        // ackSpoken se marca cuando el TTS ARRANCA (beginTts dentro de speakFiller),
        // no al agendar. Con FC_LOW_FILLER_GATE_MS=0 es el mismo tick del submit, por
        // lo que el flag ya está seteado cuando se lee para armar el request.
        this.fillerSpeakPromise = this.speakFiller(phrase, () => {
          this.ackSpoken = true;
        });
      }
    };
    if (FC_LOW_FILLER_GATE_MS <= 0) {
      fire();
      return;
    }
    this.fillerGateTimer = setTimeout(fire, FC_LOW_FILLER_GATE_MS);
  }

  private watchServerAck(stream: FcLowTurnStream, endReason: FcLowEndReason): void {
    if (!stream.ack) return;
    void stream.ack.then(
      (ack) => {
        if (!ack || this.currentStream !== stream) return;
        this.speakServerAck(ack.category, endReason);
      },
      () => undefined,
    );
  }

  private speakServerAck(category: string, endReason: FcLowEndReason): void {
    if (this.stopped || !this.submitting || this.ttsActive) return;
    if (this.host.shouldSuppressFollowupNarration?.() === true) return;
    if ((endReason !== 'eos_grace' && this.midUtterance) || this.speechDuringSubmit) return;
    const phrase = pickConnectorByCategory(category, { recent: this.recentConnectors });
    if (!phrase) return;
    this.recentConnectors = [...this.recentConnectors, phrase].slice(-FC_LOW_CONNECTOR_NO_REPEAT);
    this.fillerSpeakPromise = this.speakFiller(phrase);
  }

  /**
   * Si el conector ya arrancó, lo deja terminar ANTES de narrar (speak() cancela
   * el audio en curso: narrar encima lo cortaría a media palabra). El tope se
   * deriva del largo hablado (arranque + chars × ~90 ms, [900, 4500] ms) y se
   * cuenta desde que arrancó el conector: con un BE rápido, un tope corto
   * cortaba "Ya veo, gracias por contarme." a media frase. Solo un stop
   * dirigido por el usuario (clearFillerGate cancelTts) corta un conector.
   */
  private async awaitFillerBeforeNarration(): Promise<void> {
    const pending = this.fillerSpeakPromise;
    if (!this.fillerStarted || !pending) return;
    const elapsed = this.fillerStartedAt ? Date.now() - this.fillerStartedAt : 0;
    const remaining = Math.max(0, fillerWaitBoundMs(this.fillerSpokenChars) - elapsed);
    let t: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      pending,
      new Promise<void>((resolve) => {
        t = setTimeout(resolve, remaining);
      }),
    ]);
    if (t !== undefined) clearTimeout(t);
  }

  private async submitAccumulated(
    endReason: FcLowEndReason,
    forced?: string,
    detectedCommandHint?: FcLowDetectedCommandHint,
  ): Promise<void> {
    if (this.stopped || !this.config || this.submitting) return;
    // Choke point: jamas se POSTea un turno nuevo mientras el anterior no cerro
    // (`done`). El turno BE no es idempotente. Si el settled fallo, el camino de
    // turno fallido (ingestError) ya corrio -> abortar.
    if (this.settledGate && !(await this.settledGate)) return;
    if (this.stopped || !this.config || this.submitting) return;
    // Espiral de avances (logs3): con una respuesta de Eva PARKEADA, jamás se
    // submitea un turno nuevo — el candidato aún no escuchó esa respuesta y lo
    // que dijo mientras tanto es repetición del comando / talk-over. Sin este
    // guard, cada repetición de "pasa" viajaba como turno nuevo y LOW (ya en la
    // pregunta siguiente) avanzaba OTRA vez: idx0 → idx2 → farewell → complete.
    const parked = this.parkedResponse;
    // turn_seq de un followup/retry parkeado que el candidato nunca escuchó y que
    // se DESCARTA porque habló encima (el BE lo revierte y decide solo).
    let unheardTurnSeq: number | undefined;
    if (parked) {
      if (this.parkedIsAdvance(parked)) {
        // Avance retenido: entregarlo y DESCARTAR lo dicho durante el park
        // (no puede ser respuesta a una pregunta que no escuchó). Cubre
        // también los comandos forzados (botón/voz "pass"): el usuario pide
        // exactamente lo que ya está retenido.
        console.log('[FC] parked_advance_flush — dropping talk-over speech', {
          endReason,
          forced: !!forced,
          dropped: (this.latestTranscript || '').slice(0, 60),
        });
        this.dropOpenUtterance();
        this.cancelEndOfSpeechGrace();
        void this.flushParkedResponse();
        return;
      }
      if (!forced) {
        if (this.utteranceMateriallyGrew() && !this.parkedServesLifeline(parked)) {
          // El texto creció de verdad. Lo parkeado quedó viejo: se reenvía el
          // enunciado COMPLETO (lo ya dicho + lo nuevo), mismo utterance_id.
          if (typeof parked.turn_seq === 'number') unheardTurnSeq = parked.turn_seq;
          console.log('[FC] parked_followup_discarded — new speech wins', {
            endReason,
            unheard_turn_seq: unheardTurnSeq,
          });
        } else {
          // Sin crecimiento (o salvavidas): narrar lo parkeado. La migaja se
          // saca del buffer; si el salvavidas se narra primero, el delta queda
          // para el próximo enunciado (sealHeardUtterance).
          console.log('[FC] parked_followup_flush_first — no new speech', { endReason });
          if (!this.parkedServesLifeline(parked)) this.restoreSentUtterance();
          this.cancelEndOfSpeechGrace();
          await this.flushParkedResponse();
          if ((this.latestTranscript || '').trim()) {
            // Habla que llegó durante la narración, o el delta que el salvavidas no descartó.
            this.lastAsrActivityAt = Date.now();
            this.armEndOfSpeechGrace();
          }
          return;
        }
      } else if (typeof parked.turn_seq === 'number') {
        // El comando es otro turno: revierte a Eva, pero no pisa la respuesta.
        unheardTurnSeq = parked.turn_seq;
      }
    }
    // El cierre por silencio (eos_grace) YA verificó que pasó el umbral adaptativo
    // desde la última actividad: el flag midUtterance del componente es rezagado
    // y no debe diferirlo. Otros motivos (p.ej. answer_budget) sí esperan a que
    // el candidato suelte el piso.
    if (!forced && endReason !== 'eos_grace' && this.midUtterance) {
      this.armEndOfSpeechGrace();
      return;
    }
    this.cancelEndOfSpeechGrace();
    this.clearAbsenceWatch();

    // Plan §9.1 — truncation signal for the rubric contract. Reuses the SAME
    // util the grace timers already call (no re-implementing the heuristic).
    // Only meaningful for the candidate's own ASR transcript: a `forced` call
    // sends a synthetic sentinel (command phrase / (silencio) / (comando:*)),
    // never "cut off speech", so it is never flagged incomplete. AUXILIARY
    // only — the BE's strict `looksDanglingTranscript` decides retries, not
    // this flag (see FcLowRequest.transcript_incomplete).
    const transcriptIncomplete = !forced && looksIncompleteTranscript(this.latestTranscript);
    // Telemetría del umbral de cierre por silencio (antes de limpiar buffer/hint).
    console.log('[FC] eos_threshold', {
      profile: this.adaptationProfile(),
      base_s: this.baseSilenceMs() / 1000,
      dangling: this.eosIncompleteHint || looksIncompleteTranscript(this.latestTranscript),
      threshold_ms: this.getEosThresholdMs(),
      endReason,
    });

    const transcript = (forced ?? this.latestTranscript).trim() || EMPTY_ANSWER_FALLBACK;
    const synthetic = !!forced || transcript.startsWith('(');
    // Salvavidas — snapshot del affect engine ANTES de user_turn_complete: es
    // el estado del turno que se cierra (el léxico se resetea recién en el
    // próximo mic_open, pero así no depende de ese orden).
    const affectSignals = this.affectSignals(forced ? '' : this.latestTranscript);
    this.bargeInCount = 0;
    this.bargeInThisTts = false;
    // Solo el saludo real: un turno del módulo de experiencia previa sí lleva
    // conector y no manda greetingAckSpoken (el BE no lo lee fuera del saludo).
    const fromGreeting = this.lastTurnKind === 'greeting' && !this.lastTurnIntro;
    this.parkedResponse = null;
    this.parkedNarrateCtx = null;
    this.preludeAppliedForTurnSeq = null;
    this.eosIncompleteHint = false;
    this.submitting = true;
    this.speechDuringSubmit = false;
    // eos_grace: el cierre por silencio ya se verificó; el flag del componente es
    // rezagado → se limpia (solo habla NUEVA lo vuelve a poner en true).
    if (endReason === 'eos_grace') this.midUtterance = false;
    this.ackSpoken = false;
    if (synthetic) {
      // Comando o sentinela: no revisa la respuesta. El buffer se tira.
      this.dropOpenUtterance();
    } else {
      if (!this.utteranceId) {
        this.utteranceId = this.newUtteranceId();
        this.utteranceRevision = 1;
      } else if (unheardTurnSeq !== undefined) {
        this.utteranceRevision += 1;
      }
      // No se vacía el buffer: se vacía cuando el candidato oye a Eva.
      this.sentUtteranceText = transcript;
    }

    this.host.ingestCommand({
      type: 'user_turn_complete',
      transcript,
      assistant_turn_id: this.lastAssistantTurnId,
      questionIndex: this.lastQuestionIndex,
    });

    // Conector lingüístico entre turnos: se dispara en el mismo tick del submit
    // (gate 0 ms) para cubrir el gap del low-api. No en
    // gloria/overtime ni faltando <=15s con el candidato hablando: ahí no se
    // interrumpe (el conector cortaría al candidato que sigue hablando).
    const serverAck =
      this.serverAckMode && typeof this.deps.lowApi.submitTurnStream === 'function';
    // Revisión del mismo enunciado (siguió hablando y se descartó la respuesta
    // parkeada): el acuse ya se dijo en la revisión 1. Sin esto, cada pausa
    // sumaba otro "gracias" (eb01d98d: ~9 en una sola respuesta). ackSpoken=true
    // para que el BE tampoco anteponga su propio puente.
    const utteranceRevision = !synthetic && unheardTurnSeq !== undefined && this.utteranceRevision > 1;
    if (utteranceRevision) {
      this.clearFillerGate({ cancelTts: false });
      this.ackSpoken = true;
    } else if (serverAck) {
      this.clearFillerGate({ cancelTts: false });
    } else if (this.host.shouldSuppressFollowupNarration?.() !== true) {
      this.scheduleFillerGate(transcript, detectedCommandHint, fromGreeting, endReason);
    }

    const submitStartedAt = Date.now();
    let timeUpAfterSubmit = false;
    try {
      const stream = this.openTurn({
        transcript,
        adaptationProfile: this.adaptationProfile(),
        detectedCommandHint,
        transcriptIncomplete,
        endReason,
        affectSignals,
        // Greeting: el FE nunca habla ack local → false (el BE saluda con su nombre validado).
        ...(fromGreeting ? { greetingAckSpoken: false } : {}),
        ackSpoken: this.ackSpoken,
        ...(serverAck ? { ackMode: 'server' as const } : {}),
        ...(unheardTurnSeq !== undefined ? { unheardTurnSeq } : {}),
        ...(!synthetic && this.utteranceId
          ? { utteranceId: this.utteranceId, utteranceRevision: this.utteranceRevision }
          : {}),
        answerTimer: this.answerTimer(),
      });
      this.currentStream = stream;
      this.pendingTimeUp = false;
      this.pendingTimeUpIntro = false;
      this.awaitingDecision = true;
      if (
        serverAck &&
        !utteranceRevision &&
        endReason !== 'time_up' &&
        this.connectorKindFor(transcript, detectedCommandHint, fromGreeting)
      ) {
        this.watchServerAck(stream, endReason);
      }
      const res = await stream.decision;
      this.awaitingDecision = false;
      const decisionMs = Date.now() - submitStartedAt;
      // Gate del mic: la persistencia diferida corre en paralelo a filler+narracion.
      const gate = this.guardSettled(stream.settled, {
        startedAt: submitStartedAt,
        decisionMs,
      });
      this.settledGate = gate;
      // Solo se libera al resolver OK; un gate fallido queda (el turno ya fue a ERROR).
      void gate.then((ok) => {
        if (ok && this.settledGate === gate) this.settledGate = null;
      });
      if (this.stopped) {
        this.clearFillerGate({ cancelTts: true });
        return;
      }
      // Si el conector ya arrancó, dejarlo terminar (no cortarlo a media
      // palabra) antes de narrar; si aún no arrancó, cancelar el timer.
      await this.awaitFillerBeforeNarration();
      this.clearFillerGate({ cancelTts: false });
      if (this.stopped) return;
      if (this.pendingTimeUp) {
        const introTimeUp = this.pendingTimeUpIntro;
        this.pendingTimeUp = false;
        this.pendingTimeUpIntro = false;
        timeUpAfterSubmit = this.deferTimeUpDecision(res, introTimeUp);
        if (timeUpAfterSubmit) return;
      }
      await this.applyResponse(res);
    } catch (err) {
      this.clearFillerGate({ cancelTts: true });
      if (this.stopped) return;
      this.ingestError(err, 'No pudimos enviar tu respuesta.', false, 'submit_turn');
    } finally {
      this.awaitingDecision = false;
      this.submitting = false;
      if (timeUpAfterSubmit && !this.stopped) {
        // El pass que se perdía mientras Eva pensaba: viaja ahora, con la
        // decisión retenida como unheard_turn_seq (el BE la revierte).
        void this.submitAccumulated('time_up', PASS_TRANSCRIPT, buildDetectedCommandHint('pass'));
      } else {
        this.resumeParkedAfterSubmit();
      }
    }
  }

  /**
   * Bug dev 2026-10-07 — el reloj venció mientras Eva pensaba. ¿Se descarta la
   * decisión que llegó? Avances (pregunta nueva / farewell / complete) y el
   * salvavidas (abre ventana nueva a propósito, consume cupo sin rollback) se
   * aplican normal. Contrapregunta / retry / reformulación / repose / stale NO
   * se narran: el candidato oía "otra pregunta" y enseguida "se acabó el
   * tiempo", o quedaba con el mic abierto en silencio hasta un segundo corte.
   * Devuelve true si el caller debe mandar el pass time_up en su lugar.
   */
  private deferTimeUpDecision(res: FcLowResponse, introTimeUp = false): boolean {
    // Módulo de experiencia previa: el reloj venció mientras Eva decidía el
    // siguiente turno del módulo. Nunca es un pase (el candidato no pidió
    // saltar; el BE lo tomaba como "pasar" y saltaba a Q1 sin narrar el turno
    // 2). Se descarta el time-up y la decisión se aplica normal: el siguiente
    // turno intro se narra con ventana nueva, o intro_complete abre Q1.
    if (introTimeUp || res.phase === 'intro' || res.reason === INTRO_COMPLETE_REASON) {
      console.log('[FC] time_up_during_thinking — intro turn, time-up dropped (no pass)', {
        phase: res.phase,
        reason: res.reason,
        turn_seq: res.turn_seq,
      });
      return false;
    }
    if (this.parkedServesLifeline(res)) return false;
    if (res.reason === FC_LOW_STALE_TURN_REASON) {
      console.log('[FC] time_up_during_thinking — stale decision dropped');
      return true;
    }
    if (this.parkedIsAdvance(res)) return false;
    console.log('[FC] time_up_during_thinking — decision dropped, sending pass', {
      phase: res.phase,
      reason: res.reason,
      turn_seq: res.turn_seq,
    });
    // Se retiene como parkeada para que el pass forzado la mande como
    // unheard_turn_seq (mismo camino que un comando sobre un followup parkeado).
    this.dropOpenUtterance();
    this.cancelEndOfSpeechGrace();
    this.parkedResponse = res;
    this.parkedNarrateCtx = null;
    return true;
  }

  /**
   * Bug dev 2026-10-06 (c3162722 / 56629017): con `submitting` activo, el habla
   * del candidato no arma la grace (noteTranscript) y `onSpeechSettled` sale
   * temprano. Si habló y se calló MIENTRAS el BE decidía, la respuesta se parkea
   * al llegar y ese "ya se calló" ya se perdió: nada la liberaba (Eva muda con
   * mic abierto hasta el answer_budget). Al cerrar el submit se re-evalúa el
   * piso con el estado actual; si sigue hablando, su settled llegará después.
   */
  private resumeParkedAfterSubmit(): void {
    if (!this.parkedResponse || this.stopped || this.submitting) return;
    if (this.midUtterance) return;
    this.onSpeechSettled();
  }

  /** submitTurnStream si el api lo trae; si no, submitTurn buffered (settled inmediato). */
  private openTurn(opts: OpenTurnOptions): FcLowTurnStream {
    const api = this.deps.lowApi;
    const { testId, email, sessionId } = this.config!;
    if (typeof api.submitTurnStream === 'function') {
      // Idempotencia: índice de la última pregunta aplicada + turn_seq de la última
      // respuesta aplicada. El fallback buffered (solo dobles de test / API sin
      // stream) no los lleva: la API real siempre expone submitTurnStream.
      return api.submitTurnStream(
        buildSubmitTurnRequest(
          testId,
          email,
          sessionId,
          opts.transcript,
          opts.adaptationProfile,
          opts.detectedCommandHint,
          opts.transcriptIncomplete,
          opts.endReason,
          opts.affectSignals,
          { questionIdx: this.expectedQuestionIdx, turnSeq: this.lastAppliedTurnSeq },
          {
            greetingAckSpoken: opts.greetingAckSpoken,
            ackSpoken: opts.ackSpoken,
            ...(opts.ackMode ? { ackMode: opts.ackMode } : {}),
            unheardTurnSeq: opts.unheardTurnSeq,
            ...(opts.utteranceId
              ? { utteranceId: opts.utteranceId, utteranceRevision: opts.utteranceRevision }
              : {}),
            answerTimer: opts.answerTimer,
          },
        ),
      );
    }
    const base = [
      testId,
      email,
      sessionId,
      opts.transcript,
      opts.adaptationProfile,
      opts.detectedCommandHint,
      opts.transcriptIncomplete,
      opts.endReason,
      opts.affectSignals,
    ] as const;
    const decision = asPromise(
      api.submitTurn(...base, undefined, {
        greetingAckSpoken: opts.greetingAckSpoken,
        ackSpoken: opts.ackSpoken,
        unheardTurnSeq: opts.unheardTurnSeq,
        answerTimer: opts.answerTimer,
        ...(opts.utteranceId
          ? { utteranceId: opts.utteranceId, utteranceRevision: opts.utteranceRevision }
          : {}),
      }),
    );
    const settled = decision.then(() => undefined);
    settled.catch(() => undefined); // el rechazo ya lo consume `decision`
    return { decision, settled };
  }

  /** Promise con tope; el timer se limpia siempre. */
  private metric(name: string, value: number, unit?: 'ms' | 'count', extra?: Record<string, string | number | boolean>): void {
    try {
      if (extra) this.host.reportMetric?.(name, value, unit, extra);
      else this.host.reportMetric?.(name, value, unit);
    } catch {
      /* telemetry never breaks the turn */
    }
  }

  private withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const t = setTimeout(() => reject(new Error(`${what}: timeout ${ms}ms`)), ms);
      p.then(
        (v) => {
          clearTimeout(t);
          resolve(v);
        },
        (e) => {
          clearTimeout(t);
          reject(e);
        },
      );
    });
  }

  /**
   * Streaming por etapas — gate de apertura del mic. Espera `settled` (tope
   * FC_LOW_SETTLED_TIMEOUT_MS = 45 s; el lambda corta a 60 s y la cadena diferida
   * incluye resumen LLM + S3 + dispatch). Si falla/vence DESPUES de que el
   * candidato escucho la decision: NO se re-POSTea (el turno del BE no es
   * idempotente: upsertAnswer inline, cadenas diferidas parciales, corte tras
   * `done` del server) -> ingestError directo (paridad con un 500 de siempre) y
   * el mic NO se abre. Resuelve true = abrir mic; false = error ya reportado.
   */
  private async guardSettled(
    settled: Promise<void>,
    t: { startedAt: number; decisionMs: number },
  ): Promise<boolean> {
    try {
      await this.withTimeout(settled, FC_LOW_SETTLED_TIMEOUT_MS, 'settled');
      console.log('[FC] low_turn_timing', {
        decision_ms: t.decisionMs,
        settled_ms: Date.now() - t.startedAt,
      });
      this.metric('low_turn_decision_ms', t.decisionMs, 'ms');
      this.metric('low_turn_settled_ms', Date.now() - t.startedAt, 'ms');
      return true;
    } catch (err) {
      if (this.stopped) return false;
      console.warn('[FC] low_settled_failed — failed-turn path (no re-submit)', {
        decision_ms: t.decisionMs,
        settled_ms: Date.now() - t.startedAt,
        err: (err as any)?.message,
      });
      this.metric('low_settled_failed', 1, 'count', {
        reason: /: timeout \d+ms$/.test((err as any)?.message ?? '') ? 'timeout' : 'error',
      });
      // Turno fallido: el servicio pasa a 'error'. Cortar TTS y abortar todo lo
      // que esta en vuelo (generation++) para que nada lo saque de ese estado.
      this.turnFailed = true;
      this.generation++;
      try {
        this.deps.tts.cancel();
      } catch {
        /* ignore */
      }
      this.ingestError(err, 'No pudimos guardar tu respuesta.', true, 'submit_turn');
      return false;
    }
  }

  /** true = abrir el mic; false = persistencia fallida (ERROR ya emitido) o runtime detenido. */
  private async micGateOpen(gen: number): Promise<boolean> {
    const gate = this.settledGate;
    if (gate) {
      const ok = await gate;
      if (!ok) return false;
    }
    return !this.stopped && !this.turnFailed && gen === this.generation;
  }

  /**
   * `generic` = falla de transporte/protocolo post-decision (settled timeout,
   * stream sin done, NDJSON invalido, evento error): NUNCA se muestra texto
   * interno al candidato (detalle solo en consola). Los mensajes que el server
   * mando pre-decision (ApiError del interceptor) se siguen mostrando.
   */
  private ingestError(err: unknown, fallback: string, generic = false, action?: FcLowAction): void {
    // ApiErrors were already reported by the HTTP interceptor; the rest never reached it.
    if (!isApiError(err)) {
      reportApiError({
        endpoint: '/api/v2/fc/low',
        method: 'POST',
        ...classifyClientFailure(err),
        ...(action && { action }),
      });
    }
    if (generic || err instanceof FcLowStreamError) {
      console.warn('[FC] low_turn_error (detalle tecnico, no se muestra)', err);
      this.metric('low_turn_error', 1, 'count');
      this.host.ingestCommand({ type: 'ERROR', message: fallback });
      return;
    }
    const message =
      (err as any)?.error?.message ||
      (err as any)?.message ||
      fallback;
    this.host.ingestCommand({
      type: 'ERROR',
      message: typeof message === 'string' ? message : fallback,
    });
  }

  /** Internal — maps an fc-low response into the fc-v5 command sequence. */
  private async applyResponse(res: FcLowResponse, resumeCtx?: ApplyCtx): Promise<void> {
    if (this.stopped || this.turnFailed) return;

    // Idempotencia (BE): el estado ya avanzó respecto de lo que el FE aplicó
    // (POST duplicado / replay / reintento de red). Dos casos:
    //  - el BE está en OTRA pregunta/fase/prompt que la que el FE aplicó (el primer
    //    POST avanzó y el reintento recibió stale): se aplica como respuesta
    //    normal (prelude + narración del prompt activo) — si no, UI y audio se
    //    quedarían en Qn con el BE en Qn+1.
    //  - misma pregunta y mismo prompt: línea local "¿me lo repites?" + mic.
    if (res.reason === FC_LOW_STALE_TURN_REASON) {
      if (!this.staleMatchesApplied(res)) {
        console.warn('[FC] stale_turn con estado avanzado — se aplica como respuesta normal', {
          turn_seq: res.turn_seq,
          question_index: res.question?.index,
          applied_question_index: this.lastQuestionIndex,
        });
        this.metric('stale_turn', 1, 'count', { kind: 'advanced' });
        res = { ...res, reason: null };
      } else {
        await this.applyStaleTurn(res);
        return;
      }
    }

    const seq = typeof res.turn_seq === 'number' ? res.turn_seq : null;
    let ctx: ApplyCtx | null = null;
    this.noteBackendState(res);

    // Respuesta SILENCIOSA (echo_suspected / premature_user_turn sin texto): ni TTS, ni
    // card, ni assistant_turn_started. Solo se reabre el mic; si no, el shell quedaría
    // colgado en "procesando" tras user_turn_complete.
    if (!resumeCtx && this.isSilentRepose(res)) {
      await this.applySilentRepose(res);
      return;
    }

    if (resumeCtx) {
      // Reanudación de una continuación parkeada: solo la etapa narrate. Gen nueva
      // (mismo criterio que antes, cuando el flush reaplicaba la respuesta).
      this.clearAbsenceWatch();
      ctx = { ...resumeCtx, gen: ++this.generation };
      this.lastAssistantTurnId = ctx.assistantTurnId;
    } else {
      if (this.shouldParkForHumanFloor(res)) {
        console.log('[FC] hold_eva_for_human', {
          phase: res.phase,
          reason: res.reason,
          mid: this.midUtterance,
          duringSubmit: this.speechDuringSubmit,
        });
        this.parkedResponse = res;
        this.parkedNarrateCtx = null;
        return;
      }
      // El prelude NUNCA corre dos veces para el mismo turn_seq mientras su turno
      // no terminó de narrarse (parked / en curso).
      if (seq !== null && this.preludeAppliedForTurnSeq === seq) {
        console.log('[FC] duplicate_turn_seq_ignored', { turn_seq: seq });
        return;
      }

      // New fc-low narration — reset any prior absence ladder before speaking.
      this.clearAbsenceWatch();

      // VC detection (CONTRATO-VC-DETECTION.md §2/§4.2) — cierre por detección de
      // videoconferencia decidido client-side (conferencingLadder). A diferencia
      // de cualquier otra razón de complete(), NO sigue el camino genérico de
      // next_question/mic_open/interview_complete de abajo: emite
      // `reason:'aborted' + termination_reason:'conferencing_tool'` (mismo
      // mapeo que usa el agente Python en HIGH — outbound.py, contrato §4.3)
      // para que fc-session.component.ts muestre viewState='suspended' (pantalla
      // neutra ya agregada por otro agente) en vez de la de error genérico.
      if (res.reason === 'conferencing_tool') {
        await this.applyConferencingSuspend(res);
        return;
      }
      this.preludeAppliedForTurnSeq = seq;
    }

    let parked = false;
    try {
      // Etapa 1 — prelude (budgets, criteria_eval, ack, next_question, transición).
      if (!ctx) {
        ctx = await this.applyPrelude(res);
        if (!ctx) return;
      }
      // Etapa 2 — narrate (assistant_turn_started, TTS, mic_open). Puede parkearse
      // como continuación si el humano retoma el piso antes del TTS.
      parked = (await this.narrateStage(res, ctx)) === 'parked';
    } finally {
      if (!parked) this.preludeAppliedForTurnSeq = null;
    }
  }

  /** repose del BE sin nada que decir ni pintar (turno eco / prematuro descartado). */
  private isSilentRepose(res: FcLowResponse): boolean {
    if (resolvePromptAction(res) !== 'repose') return false;
    if (res.interview_complete || res.phase === 'complete' || res.phase === 'farewell') return false;
    return !(res.narration_text || '').trim() && !(res.display_text || '').trim();
  }

  /**
   * Turno silencioso: el BE descartó el turno (eco/prematuro) y NO habla. Reabre el
   * mic con los mismos presupuestos (como stale_turn pero sin línea local) y rearma
   * la vigilancia de ausencia. SIEMPRE reabre (ignora mic_open=false): quedarse
   * mudo sin mic = entrevista colgada. No toca el prompt vigente ni lastDisplayText.
   */
  private async applySilentRepose(res: FcLowResponse): Promise<void> {
    this.sealHeardUtterance();
    console.log('[FC] silent_repose — sin voz, se reabre el mic', {
      reason: res.reason,
      turn_seq: res.turn_seq,
    });
    if (res.question && typeof res.question.index === 'number') {
      this.lastQuestionIndex = res.question.index;
    }
    const gen = this.generation;
    if (!(await this.micGateOpen(gen))) return;
    this.markAnswerWindowStart();
    this.host.ingestCommand({
      type: 'mic_open',
      thinking_time_s: 0,
      answer_budget_s: this.lastBudgets.answer_s,
      turn_silence_s: this.lastTurnSilenceS,
      ...this.confirmAdvanceFlag(),
      questionIndex: this.lastQuestionIndex,
      assistant_turn_id: this.lastAssistantTurnId,
    });
    if (
      this.lastTurnKind === 'question' ||
      this.lastTurnKind === 'followup' ||
      this.lastTurnKind === 'rephrase'
    ) {
      this.startAbsenceWatch(true);
    }
  }

  /** Punteros de idempotencia: el estado del BE tras esta respuesta (boot / start / submit). */
  private noteBackendState(res: FcLowResponse): void {
    if (res.ack_stream === true) this.serverAckMode = true;
    if (typeof res.turn_seq === 'number') this.lastAppliedTurnSeq = res.turn_seq;
    // Nombre validado por el BE: ausente (BE viejo) conserva el anterior; null/'' = sin nombre.
    if (res.candidate_name !== undefined) {
      this.lastCandidateName =
        typeof res.candidate_name === 'string' && res.candidate_name.trim()
          ? res.candidate_name.trim()
          : null;
    }
    if (res.question && typeof res.question.index === 'number') {
      this.expectedQuestionIdx = res.question.index;
    }
  }

  /**
   * ¿El estado que informa un stale_turn coincide con lo que el FE ya aplicó?
   * false (= estado avanzado) si cambió el índice de pregunta, la fase o el texto
   * del prompt activo (uno contiene al otro = mismo: el BE puede mandar solo el
   * enunciado y el FE haber narrado ack + enunciado). Con una respuesta parkeada el
   * flush entregará el estado: se trata como coincidente (no se duplica).
   */
  private staleMatchesApplied(res: FcLowResponse): boolean {
    if (this.parkedResponse) return true;
    const idx = res.question?.index;
    if (typeof idx === 'number' && idx !== this.lastQuestionIndex) return false;
    if (res.phase && this.lastAppliedPhase && res.phase !== this.lastAppliedPhase) return false;
    const text = normalizeForEcho(res.narration_text || res.display_text || '').join(' ');
    if (!text) return true;
    const sameAs = (applied: string) => {
      const a = normalizeForEcho(applied).join(' ');
      return !!a && (a.includes(text) || text.includes(a));
    };
    return sameAs(this.lastNarrationText) || sameAs(this.lastDisplayText);
  }

  /**
   * stale_turn de la MISMA pregunta y prompt: el BE no avanzó nada que el
   * candidato no haya oído. Se re-sincronizan los punteros, se dice una línea
   * local corta ("Perdona, ¿me lo repites?" — su respuesta se perdió) y se REABRE
   * el mic con los mismos presupuestos (el shell quedó en "procesando" tras
   * user_turn_complete). Sin ack/next_question/narración del BE.
   */
  private async applyStaleTurn(res: FcLowResponse): Promise<void> {
    this.sealHeardUtterance();
    this.metric('stale_turn', 1, 'count', { kind: 'same_question' });
    console.warn('[FC] stale_turn — misma pregunta: pedir repetir + reabrir mic', {
      turn_seq: res.turn_seq,
      question_index: res.question?.index,
    });
    this.noteBackendState(res);
    if (res.question && typeof res.question.index === 'number') {
      this.lastQuestionIndex = res.question.index;
    }
    if (res.mic_open === false) return;
    const gen = this.generation;
    if (!this.humanHoldsFloor()) {
      this.host.setLocalAudioPlaying?.(true);
      this.beginTts(FC_LOW_STALE_REPEAT_LINE, false);
      try {
        await this.deps.tts.speak(FC_LOW_STALE_REPEAT_LINE);
      } catch {
        /* TTS de la línea local no es fatal */
      }
      this.endTts();
      this.host.setLocalAudioPlaying?.(false);
    }
    if (!(await this.micGateOpen(gen))) return;
    this.markAnswerWindowStart();
    this.host.ingestCommand({
      type: 'mic_open',
      thinking_time_s: 0,
      answer_budget_s: this.lastBudgets.answer_s,
      turn_silence_s: this.lastTurnSilenceS,
      ...this.confirmAdvanceFlag(),
      questionIndex: this.lastQuestionIndex,
      assistant_turn_id: this.lastAssistantTurnId,
    });
    if (this.lastTurnKind === 'question' || this.lastTurnKind === 'followup' || this.lastTurnKind === 'rephrase') {
      this.startAbsenceWatch(true);
    }
  }

  /**
   * Etapa 1 de applyResponse. Devuelve el contexto para la etapa narrate, o null si
   * el turno quedó resuelto acá (greeting two-beat) o abortado (stop / gen vieja).
   */
  private async applyPrelude(res: FcLowResponse): Promise<ApplyCtx | null> {
    // Boot resume tras recarga: el back informa cuanto tiempo real ya pasó de
    // la sesión — sin esto el cronómetro del shell arrancaba de 0 en cada
    // reload en vez de seguir desde donde iba (bug reportado en producción).
    if (typeof res.elapsed_seconds === 'number') {
      this.host.ingestCommand({ type: 'timer_resync', elapsed_seconds: res.elapsed_seconds });
    }

    const gen = ++this.generation;
    const turnSeq = typeof res.turn_seq === 'number' ? res.turn_seq : gen;
    const assistantTurnId = `low-${turnSeq}`;
    this.lastAssistantTurnId = assistantTurnId;
    this.lastAppliedPhase = res.phase ?? null;

    const isIntroTurn = res.phase === 'intro';
    // Intro: sin piense (como el saludo) si el BE no manda budgets.
    const thinkS =
      typeof res.budgets?.think_s === 'number' && res.budgets.think_s >= 0
        ? res.budgets.think_s
        : isIntroTurn
          ? 0
          : DEFAULT_THINK_S;
    const answerS =
      typeof res.budgets?.answer_s === 'number' && res.budgets.answer_s > 0
        ? res.budgets.answer_s
        : DEFAULT_ANSWER_S;
    this.lastBudgets = { think_s: thinkS, answer_s: answerS };

    const rawSilenceS =
      typeof res.turn_silence_s === 'number' && res.turn_silence_s >= 0
        ? res.turn_silence_s
        : DEFAULT_TURN_SILENCE_S;
    // Intro: el candidato narra su experiencia con pausas para pensar (sin zona de piense) → piso 5s.
    // Confirmación de avance: mismo piso, el candidato decide si agrega algo antes de hablar.
    this.confirmAdvanceActive = res.active_prompt_kind === 'confirm_advance';
    this.cancelConfirmAdvanceNoSpeech();
    this.grantedPauseS = grantedPauseSeconds(res);
    this.grantedSessionRemainingS = grantedSessionRemainingSeconds(res);
    const silenceS = Math.max(rawSilenceS, turnSilenceFloorS(isIntroTurn, this.confirmAdvanceActive));
    this.lastTurnSilenceS = silenceS;

    if (typeof res.total_questions === 'number' && res.total_questions > 0) {
      this.lastTotalQuestions = res.total_questions;
    }

    const narration = (res.narration_text || res.display_text || '').trim();
    const display = (res.display_text || '').trim();
    // Prefer explicit display for thread paint; fall back to narration only if empty.
    const displayOrNarration = display || narration;
    // 'repose' = el BE re-planteó el MISMO prompt (no hay texto nuevo que pintar).
    const promptAction = resolvePromptAction(res);
    const isRepose = promptAction === 'repose';
    this.lastNarrationText = narration || displayOrNarration;
    if (!isRepose || !this.lastDisplayText) this.lastDisplayText = displayOrNarration;
    this.lastTurnWasContext =
      res.reason === 'client_context' || res.reason === 'client_context_followup';

    const question = res.question;
    // Compute same-idx BEFORE updating lastQuestionIndex (followup stacking guard).
    const sameIdx =
      !!question &&
      typeof question.index === 'number' &&
      this.lastQuestionIndex === question.index;
    if (question && typeof question.index === 'number') {
      this.lastQuestionIndex = question.index;
    }

    // Panel dev de criterios: traducir criteria_eval del low-api al evento que
    // consume el criteria-panel (paridad con HIGH). Solo llega en local/dev.
    if (res.criteria_eval) {
      this.host.ingestCommand({
        type: 'criteria_eval',
        question_idx: this.lastQuestionIndex ?? 0,
        question_id: question?.id ?? '',
        decision_kind: res.criteria_eval.decision_kind,
        decision_reason: res.criteria_eval.decision_reason,
        missing_aspect: res.criteria_eval.missing_aspect,
        followup_text: res.phase === 'followup' ? displayOrNarration : '',
        criteria: res.criteria_eval.criteria,
        // DEBUG salvavidas — índice de apuro + momento de pérdida evaluado.
        ...(typeof res.criteria_eval.distress_score === 'number'
          ? { distress_score: res.criteria_eval.distress_score }
          : {}),
        ...(res.criteria_eval.lifeline_trigger !== undefined
          ? { lifeline_trigger: res.criteria_eval.lifeline_trigger }
          : {}),
      });
    }

    // High narrates greeting ACK (commands briefing) then Q1 as two beats.
    // BE greeting_complete: narration_text = ack ONLY, display_text = question.
    // intro_complete (cierre del módulo de experiencia previa) = mismo contrato.
    if ((res.reason === 'greeting_complete' || res.reason === INTRO_COMPLETE_REASON) && question) {
      await this.applyGreetingCompleteTwoBeat(res, {
        gen,
        assistantTurnId,
        thinkS,
        answerS,
        silenceS,
        narration,
        display,
        displayOrNarration,
        question,
      });
      return null;
    }

    const isFollowupPhase = res.phase === 'followup';
    // Salvavidas (PLAN_LIFELINE.md §6): retry_same de la MISMA pregunta con una
    // pista de enfoque. Se pinta como la card de contrapregunta (badge
    // "Salvavidas" en el thread) y su mic_open arma una ventana NUEVA aunque
    // el presupuesto de respuesta ya haya vencido (ver mic_open más abajo).
    const isLifeline = res.reason === LIFELINE_HINT_REASON;
    // Voz = caja: la card muestra exactamente lo que Eva dice (prefijo
    // "Te doy un salvavidas…" incluido), no el enunciado del retry_same.
    const lifelineText = isLifeline ? narration || displayOrNarration : '';
    if (isLifeline && lifelineText) this.lastDisplayText = lifelineText;
    const lifelineFields = {
      ...(isLifeline ? { reason: LIFELINE_HINT_REASON } : {}),
      ...(res.lifeline ? { lifeline: res.lifeline } : {}),
    };
    const spokenOrDisplay = displayOrNarration;
    const looksLikeFollowup =
      isFollowupPhase ||
      isLifeline ||
      (sameIdx &&
        res.phase === 'question' &&
        !!spokenOrDisplay &&
        !!question &&
        spokenOrDisplay !== (question.text || '').trim());
    // Decide maps client_clarify → phase=followup + display=original enunciado.
    // Synthesize turn_kind=rephrase so shared shell runs rephraseThread chrome.
    // prompt_action 'rewrite_question' = el BE reformuló el enunciado (mismo camino).
    const isRephrase =
      !isRepose && (res.reason === 'client_clarify' || promptAction === 'rewrite_question');
    // Repeat desde el low-api: el candidato pidió "repetí la pregunta" DURANTE la
    // ventana de respuesta, así que el texto viajó como transcript y el low-api lo
    // resolvió como retry_same con reason=candidate_repeat + narration
    // "Te repito la pregunta: <enunciado>" (phase=followup). NO es una
    // contrapregunta nueva — solo hay que re-narrar el audio, sin empujar card al
    // thread. Se marca renarration:true (mismo mecanismo que el comando local
    // repeatLastTurn) para que el shell salte pushFollowupThread.
    // prompt_action manda; sin él (BE viejo) valen las reasons de siempre.
    const isRepeat =
      isRepose ||
      (!promptAction &&
        (res.reason === 'candidate_repeat' ||
          res.reason === 'premature_user_turn' ||
          res.reason === 'noop'));

    // 1. next_question only for real advances (never followup / rephrase).
    const advancedQuestion =
      !looksLikeFollowup &&
      !isRephrase &&
      !isRepose &&
      !!question &&
      res.phase !== 'greeting' &&
      res.phase !== 'intro';
    if (advancedQuestion) {
      // Idempotencia de comandos repetidos ("quiero pasar" ×3): los finales
      // TARDÍOS del ASR de la pregunta anterior pueden llegar después de que
      // este avance se aplicó (no alcanzaron a parkear la respuesta). Sin esta
      // limpieza quedaban en el buffer y se submiteaban como turno de la
      // pregunta NUEVA → LOW los leía como otro pass → salto en cadena. Lo
      // dicho antes de narrar la pregunta nueva no puede ser su respuesta.
      if ((this.latestTranscript || '').trim()) {
        console.log('[FC] advance_clears_stale_speech', {
          dropped: this.latestTranscript.slice(0, 60),
        });
      }
      this.latestTranscript = '';
      this.windowWordBase = 0;
      this.finalizedTranscript = '';
      this.cancelEndOfSpeechGrace();
    }
    if (advancedQuestion && question) {
      // Narrar la CONCLUSIÓN de la pregunta anterior ANTES del overlay Q→Q: se
      // dice sobre el contexto de la pregunta que se deja (el thread aún muestra
      // esa pregunta + la respuesta), así no suena pegada a la pregunta nueva y
      // no confunde. Recién después viene el overlay y la pregunta nueva.
      const ack = (res.advance_ack || '').trim();
      if (ack && this.hasNarratedAQuestion) {
        // El puente ya es la voz que el candidato va a oír. El chip se
        // compromete a "hablando" desde la primera sílaba. next_question no
        // baja ese estado: si lo hiciera, entre el puente y la pregunta el
        // chip diría "pensando".
        this.host.setAgentState?.('narrating');
        this.host.setLocalAudioPlaying?.(true);
        this.beginTts(ack, false); // el ack también se registra: su eco llega tarde (bug doble ack)
        try {
          await this.deps.tts.speak(ack);
        } catch {
          /* TTS del ack no es fatal */
        }
        this.endTts();
        this.host.setLocalAudioPlaying?.(false);
        if (this.stopped || gen !== this.generation) return null;
      }

      // Mirror fc-v5 wire aliases (question_text/knowledge_name/question_index)
      // so the same component path as high arms timers + paints the thread.
      const totalQ =
        typeof res.total_questions === 'number'
          ? res.total_questions
          : this.lastTotalQuestions;
      this.host.ingestCommand({
        type: 'next_question',
        questionIndex: question.index,
        question_index: question.index,
        question_idx: question.index,
        question_text: question.text,
        text: question.text,
        areaName: question.knowledge,
        knowledge_name: question.knowledge,
        knowledge_id: question.knowledge_id,
        thinking_time_s: thinkS,
        answer_budget_s: answerS,
        question_id: question.id,
        ...(typeof totalQ === 'number' ? { total_questions: totalQ } : {}),
      } as any);

      // Respiro entre el puente y la pregunta. Si el puente ya comprometió
      // 'narrating', next_question no pinta el overlay "Pasando…": el chip
      // sigue en "hablando". Sin puente (primera pregunta) sí hay overlay.
      // Abortable si el turno cambió (stop/barge/nueva respuesta).
      if (this.hasNarratedAQuestion) {
        await new Promise((r) => setTimeout(r, FC_LOW_TRANSITION_MS));
        if (this.stopped || gen !== this.generation) return null;
      }
      this.hasNarratedAQuestion = true;
    }

    return {
      gen,
      assistantTurnId,
      thinkS,
      answerS,
      silenceS,
      narration,
      display,
      displayOrNarration,
      question,
      advancedQuestion,
      looksLikeFollowup,
      isRephrase,
      isRepeat,
      isLifeline,
      lifelineText,
      lifelineFields,
      promptAction,
    };
  }

  /**
   * Etapa 2 de applyResponse (narración). 'parked' = retenida como continuación
   * (el flush reanuda acá, sin re-correr el prelude); 'done' = terminó o abortó.
   */
  private async narrateStage(res: FcLowResponse, ctx: ApplyCtx): Promise<'parked' | 'done'> {
    const {
      gen,
      assistantTurnId,
      thinkS,
      answerS,
      silenceS,
      narration,
      display,
      displayOrNarration,
      question,
      advancedQuestion,
      looksLikeFollowup,
      isRephrase,
      isRepeat,
      isLifeline,
      lifelineText,
      lifelineFields,
      promptAction,
    } = ctx;

    // 2. assistant_turn_started FIRST (high: narration start before followup card).
    // turn_kind must mirror phase — component prefers turn_kind over phase.
    // Greeting: do NOT put question text in `text` (showCommandsHint needs empty Q).
    // Question: text = display_text (question only), never transition prefix.
    // Followup: text required for pushFollowupThread.
    // Rephrase: text/display = simplified narration for rephraseThread erase/rewrite.
    // TTS still uses full narration_text.
    const mappedPhase = isRephrase
      ? 'question'
      : looksLikeFollowup
        ? 'followup'
        : mapPhase(res.phase);
    const turnKind = isRephrase
      ? 'rephrase'
      : mappedPhase === 'followup'
        ? 'followup'
        : mappedPhase === 'greeting'
          ? 'greeting'
          : mappedPhase === 'farewell'
            ? 'farewell'
            : 'question';
    this.lastTurnKind = turnKind;
    const isIntroTurn = res.phase === 'intro';
    this.lastTurnIntro = isIntroTurn;
    this.lastIntroProgress = isIntroTurn && res.intro ? res.intro : null;
    const introField = this.lastIntroProgress ? { intro: this.lastIntroProgress } : {};

    const rephraseText = (narration || displayOrNarration).trim();
    const startedText = isRephrase
      ? rephraseText
      : mappedPhase === 'greeting'
        ? ''
        : mappedPhase === 'followup'
          ? lifelineText || displayOrNarration
          : display || question?.text || narration;
    const startedDisplay = isRephrase
      ? rephraseText
      : narration || displayOrNarration;

    // Keep lastDisplayText coherent for repeat / absence chrome.
    if (isRephrase && rephraseText) {
      this.lastDisplayText = rephraseText;
    }

    // No contrapreguntas faltando <=15s o en gloria/overtime: no narrar ni
    // pintar el followup; reabrir el mic para que el candidato termine y dejar
    // que el reloj (o la gloria) avance a la siguiente pregunta.
    // El salvavidas NO se suprime: justo llega cuando el candidato está por
    // perder la pregunta (P4/no_progress suele caer con el reloj vencido).
    const suppressFollowup =
      looksLikeFollowup &&
      !isRephrase &&
      !isRepeat &&
      !isLifeline &&
      this.host.shouldSuppressFollowupNarration?.() === true;
    if (this.shouldParkForHumanFloor(res)) {
      console.log('[FC] hold_eva_for_human', {
        phase: res.phase,
        reason: res.reason,
        when: 'before_tts',
        mid: this.midUtterance,
        duringSubmit: this.speechDuringSubmit,
      });
      // CONTINUACIÓN: el prelude (ack, criteria_eval, next_question, transición)
      // YA corrió. Se retiene solo la narración; el flush reanuda acá y jamás
      // re-corre el prelude (antes repetía ack + next_question: ack doble).
      this.parkedResponse = res;
      this.parkedNarrateCtx = ctx;
      // El puente, si sonó, ya terminó. No hay narración en curso: la fase
      // visible vuelve a "pensando" hasta que el flush hable la pregunta.
      // Sin este retorno el chip queda en "hablando" mientras el candidato
      // sigue con el piso y Eva está en silencio.
      this.host.setAgentState?.('processing');
      return 'parked';
    }

    // A partir de acá el candidato oye a Eva (o el turno se cierra sin narrar
    // el followup). El enunciado abierto termina. El delta, si quedó, sigue.
    this.sealHeardUtterance();

    if (suppressFollowup) {
      if (res.mic_open) {
        if (!(await this.micGateOpen(gen))) return 'done';
        this.markAnswerWindowStart();
        this.host.ingestCommand({
          type: 'mic_open',
          thinking_time_s: 0,
          answer_budget_s: answerS,
          turn_silence_s: silenceS,
          ...this.confirmAdvanceFlag(),
          questionIndex: question?.index ?? this.lastQuestionIndex,
          assistant_turn_id: assistantTurnId,
          question_id: question?.id,
        });
        this.startAbsenceWatch(true);
      }
      return 'done';
    }

    this.host.ingestCommand({
      type: 'assistant_turn_started',
      text: startedText,
      display_text: startedDisplay,
      phase: mappedPhase,
      turn_kind: turnKind,
      ...(isRepeat ? { renarration: true } : {}),
      // repose / rewrite_followup: el shell NO apila card nueva (ver component).
      ...(promptAction === 'repose' || promptAction === 'rewrite_followup'
        ? { prompt_action: promptAction }
        : {}),
      ...lifelineFields,
      ...introField,
      questionIndex: question?.index ?? this.lastQuestionIndex,
      question_id: question?.id,
      assistant_turn_id: assistantTurnId,
    });

    // 3. Mark TTS active, then emit followup_requested (high question.py ~1786–1806:
    // card paints together with Eva's voice — never before narration start).
    // Skip for rephrase (shell uses rephraseThread, not followup card).
    this.host.setLocalAudioPlaying?.(true);
    this.beginTts(narration || displayOrNarration);

    if (looksLikeFollowup && !isRephrase && !isRepeat) {
      const text = (lifelineText || display || narration || question?.text || '').trim();
      if (text) {
        this.host.ingestCommand({
          type: 'followup_requested',
          text,
          phase: 'question',
          ...lifelineFields,
          questionIndex: question?.index ?? this.lastQuestionIndex,
          question_id: question?.id,
        });
      }
    }

    // 4. TTS — full narration (may include “Perfecto, pasemos…” + question)
    try {
      await this.deps.tts.speak(narration || displayOrNarration);
    } catch {
      /* TTS errors are non-fatal — continue command sequence */
    }
    this.endTts();

    // Stale generation / stopped / soft barge-in → abort remaining emits.
    if (this.stopped || gen !== this.generation) {
      this.host.setLocalAudioPlaying?.(false);
      return 'done';
    }

    // 5. assistant_logical_complete
    this.host.ingestCommand({
      type: 'assistant_logical_complete',
      assistant_turn_id: assistantTurnId,
      requires_playback_ack: false,
      questionIndex: question?.index ?? this.lastQuestionIndex,
      question_id: question?.id,
    });

    // 6. playback end
    this.host.notifyPlaybackEnd();
    this.host.setLocalAudioPlaying?.(false);

    // 7. interview complete
    if (
      res.interview_complete ||
      res.phase === 'complete' ||
      res.phase === 'farewell'
    ) {
      // El cierre solo es valido si la persistencia diferida (save_session /
      // mark_completed / dispatch_evaluation) termino OK; si no, ERROR ya emitido.
      if (!(await this.micGateOpen(gen))) return 'done';
      this.host.ingestCommand({
        type: 'interview_complete',
        reason: res.reason || 'completed',
        assistant_turn_id: assistantTurnId,
      });
      return 'done';
    }

    // 8. mic_open — including interactive greeting (NO auto start_questions).
    // Followup/rephrase: think_s=0 (candidate already answered once; open answer
    // window immediately). Always send numeric budgets (defaults applied above).
    if (res.mic_open) {
      // Streaming por etapas: el mic espera narracion (ya terminada) Y settled.
      if (!(await this.micGateOpen(gen))) return 'done';
      // Cinturón del clear de avance: finales tardíos del ASR que llegaron
      // DURANTE la narración de la pregunta nueva (post-clear inicial) tampoco
      // son respuesta de ella — se limpian justo antes de abrir el mic. Solo en
      // avances: followup/rephrase conservan el buffer a propósito (continuación
      // retenida del flujo de parking).
      if (advancedQuestion && (this.latestTranscript || '').trim()) {
        console.log('[FC] mic_open_clears_stale_speech', {
          dropped: this.latestTranscript.slice(0, 60),
        });
        this.latestTranscript = '';
        this.windowWordBase = 0;
        this.finalizedTranscript = '';
        this.cancelEndOfSpeechGrace();
      }
      const micThinkS = looksLikeFollowup || isRephrase ? 0 : thinkS;
      const pauseFlag = this.beginGrantedPause();
      this.markAnswerWindowStart();
      this.host.ingestCommand({
        type: 'mic_open',
        thinking_time_s: micThinkS,
        answer_budget_s: answerS,
        turn_silence_s: silenceS,
        ...this.confirmAdvanceFlag(),
        ...pauseFlag,
        // Salvavidas: reason viaja en el mic_open para que el shell arme una
        // ventana de respuesta NUEVA (misma excepción que greeting_name_retry,
        // PR 7309) — si no, con answerBudgetExpired=true (P4/no_progress) el
        // mic_open caía en cutOffAndAdvance() y la pista no se podía usar.
        ...(isLifeline ? { reason: LIFELINE_HINT_REASON } : {}),
        // Módulo de experiencia previa: cada turno intro es una ventana NUEVA
        // con sus budgets (think 20 / answer 120). Sin esto el shell lo trataba
        // como renarración de la misma "pregunta" y reanudaba el remanente
        // PAUSADO del saludo (15 s) → ~10 s + 10 s de gloria y corte.
        ...(isIntroTurn ? { fresh_window: true } : {}),
        questionIndex: question?.index ?? this.lastQuestionIndex,
        assistant_turn_id: assistantTurnId,
        question_id: question?.id,
      });

      // Absence ladder: question/followup/rephrase answer window only — never greeting.
      // Durante la pausa no corre (el silencio es el que el candidato pidió).
      if (this.pauseActive) return 'done';
      if (
        this.lastTurnKind === 'question' ||
        this.lastTurnKind === 'followup' ||
        this.lastTurnKind === 'rephrase'
      ) {
        this.startAbsenceWatch(micThinkS <= 0 || looksLikeFollowup || isRephrase);
      }
    }
    return 'done';
  }

  /**
   * Two-beat greeting_complete: ack TTS (commands briefing) THEN Q1 paint+TTS.
   * Keeps showCommandsHint alive during beat 1 (empty currentQuestionText).
   */
  private async applyGreetingCompleteTwoBeat(
    res: FcLowResponse,
    ctx: {
      gen: number;
      assistantTurnId: string;
      thinkS: number;
      answerS: number;
      silenceS: number;
      narration: string;
      display: string;
      displayOrNarration: string;
      question: NonNullable<FcLowResponse['question']>;
    },
  ): Promise<void> {
    const {
      gen,
      assistantTurnId,
      thinkS,
      answerS,
      silenceS,
      narration,
      display,
      displayOrNarration,
      question,
    } = ctx;
    const ackText = (narration || displayOrNarration).trim();
    const questionSpeak = (display || question.text || '').trim();
    this.lastTurnIntro = false;
    this.lastIntroProgress = null;

    // Defensa (intro_complete): si el BE no separó el acuse y la narración ES
    // el enunciado, se salta el beat 1 para no leer la pregunta dos veces.
    const skipAckBeat = !ackText || ackText === questionSpeak;

    if (!skipAckBeat) {
      // --- Beat 1: greeting ACK (commands briefing) — no next_question yet ---
      this.lastTurnKind = 'greeting';
      this.lastNarrationText = ackText;
      this.lastDisplayText = ackText;

      this.host.ingestCommand({
        type: 'assistant_turn_started',
        text: '',
        display_text: ackText,
        phase: 'greeting',
        turn_kind: 'greeting',
        questionIndex: question.index,
        question_id: question.id,
        assistant_turn_id: assistantTurnId,
      });

      this.host.setLocalAudioPlaying?.(true);
      this.beginTts(ackText, false);
      try {
        await this.deps.tts.speak(ackText);
      } catch {
        /* TTS errors are non-fatal */
      }
      this.endTts();

      if (this.stopped || gen !== this.generation) {
        this.host.setLocalAudioPlaying?.(false);
        return;
      }

      this.host.ingestCommand({
        type: 'assistant_logical_complete',
        assistant_turn_id: assistantTurnId,
        requires_playback_ack: false,
        questionIndex: question.index,
        question_id: question.id,
      });
      this.host.notifyPlaybackEnd();
      this.host.setLocalAudioPlaying?.(false);
    }

    // --- Beat 2: next_question + question turn + question-only TTS ---
    const totalQ =
      typeof res.total_questions === 'number'
        ? res.total_questions
        : this.lastTotalQuestions;
    this.host.ingestCommand({
      type: 'next_question',
      questionIndex: question.index,
      question_index: question.index,
      question_idx: question.index,
      question_text: question.text,
      text: question.text,
      areaName: question.knowledge,
      knowledge_name: question.knowledge,
      knowledge_id: question.knowledge_id,
      thinking_time_s: thinkS,
      answer_budget_s: answerS,
      question_id: question.id,
      ...(typeof totalQ === 'number' ? { total_questions: totalQ } : {}),
    } as any);

    this.lastTurnKind = 'question';
    this.lastNarrationText = questionSpeak;
    this.lastDisplayText = questionSpeak;
    // Q1 ya se narró (por el two-beat) → habilitar el overlay de transición para
    // el PRIMER avance real (Q1→Q2). Sin esto, el primer cambio de pregunta no
    // mostraba el overlay (bug: la flag solo se seteaba en el path de avance).
    this.hasNarratedAQuestion = true;

    this.host.ingestCommand({
      type: 'assistant_turn_started',
      text: questionSpeak,
      display_text: questionSpeak,
      phase: 'question',
      turn_kind: 'question',
      questionIndex: question.index,
      question_id: question.id,
      assistant_turn_id: assistantTurnId,
    });

    this.host.setLocalAudioPlaying?.(true);
    this.beginTts(questionSpeak);
    try {
      await this.deps.tts.speak(questionSpeak);
    } catch {
      /* TTS errors are non-fatal */
    }
    this.endTts();

    if (this.stopped || gen !== this.generation) {
      this.host.setLocalAudioPlaying?.(false);
      return;
    }

    this.host.ingestCommand({
      type: 'assistant_logical_complete',
      assistant_turn_id: assistantTurnId,
      requires_playback_ack: false,
      questionIndex: question.index,
      question_id: question.id,
    });
    this.host.notifyPlaybackEnd();
    this.host.setLocalAudioPlaying?.(false);

    if (res.mic_open) {
      if (!(await this.micGateOpen(gen))) return;
      this.markAnswerWindowStart();
      this.host.ingestCommand({
        type: 'mic_open',
        thinking_time_s: thinkS,
        answer_budget_s: answerS,
        turn_silence_s: silenceS,
        ...this.confirmAdvanceFlag(),
        questionIndex: question.index,
        assistant_turn_id: assistantTurnId,
        question_id: question.id,
      });
      this.startAbsenceWatch(thinkS <= 0);
    }
  }

  // ---------------------------------------------------------------------
  // VC detection (CONTRATO-VC-DETECTION.md §2/§4.2/ADENDA) — conferencingLadder
  // ---------------------------------------------------------------------
  //
  // Prioridad / coexistencia con absenceLadder: AUSENCIA GANA. Si el candidato
  // no está presente no tiene sentido avisarle de una videoconferencia — y
  // dos voces (aviso de ausencia + aviso de VC) pisándose es peor que un
  // aviso de VC tarde. `handleVcState` defiere (no arma nada, ni cuenta
  // tiempo de gracia) mientras: hay un submit en vuelo, Eva ya está hablando
  // de CUALQUIER cosa (ttsActive), un check-in de ausencia está en curso, o
  // la absence ladder QUERRÍA actuar en este instante (absenceWantsToActNow).
  // Como VcScorerService reemite `state$` al menos cada 5s (su tick interno),
  // no hace falta un retry timer propio: el próximo emit reintenta solo.

  private vcStrikesStorageKey(): string {
    return `vc-strikes-fc:${this.config?.testId ?? ''}`;
  }

  private loadVcStrikes(): void {
    this.vcStrikes = 0;
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      const raw = window.sessionStorage.getItem(this.vcStrikesStorageKey());
      const n = raw ? parseInt(raw, 10) : 0;
      this.vcStrikes = Number.isFinite(n) && n > 0 ? n : 0;
    } catch {
      /* sessionStorage no disponible (SSR/privacidad) — arranca en 0 */
    }
  }

  private persistVcStrikes(): void {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.setItem(this.vcStrikesStorageKey(), String(this.vcStrikes));
    } catch {
      /* best-effort — un candidato en modo privado sigue funcionando, solo
       * pierde la persistencia entre reloads. */
    }
  }

  private subscribeVcScorer(): void {
    if (!this.deps.vcScorer) return;
    this.vcSubscription = this.deps.vcScorer.state$.subscribe((state) => this.handleVcState(state));
  }

  private unsubscribeVcScorer(): void {
    this.vcSubscription?.unsubscribe();
    this.vcSubscription = null;
  }

  private clearVcGraceTimer(): void {
    if (this.vcGraceTimer != null) {
      clearTimeout(this.vcGraceTimer);
      this.vcGraceTimer = null;
    }
  }

  /** True si la absence ladder actuaría (checkin o finalize) en este instante
   *  — usado como desempate de prioridad: "ausencia gana". */
  private absenceWantsToActNow(): boolean {
    if (!this.absenceWatchEnabled || !this.answerWindowActive || this.absenceStartedAtMs == null) {
      return false;
    }
    const absentForS = (Date.now() - this.absenceStartedAtMs) / 1000;
    return absenceNextAction(absentForS, this.absenceCheckinsDone) !== 'wait';
  }

  /** Reacciona a cada emisión de VcScorerService.state$ — decide wait/warn/suspend. */
  private handleVcState(state: VcScoreState): void {
    if (this.stopped || this.vcSuspending || this.vcWarning) return;
    // Un submit ya en vuelo (respuesta normal del candidato, u otro cierre
    // forzado) — nunca pisarlo. submitAccumulated() no-opea en silencio si
    // `this.submitting` ya es true, así que decidir ACÁ evita marcar
    // vcSuspending=true y quedar bloqueados para siempre esperando un submit
    // que nunca se va a disparar.
    if (this.submitting) return;
    if (Date.now() < this.vcQuietUntilMs) return;
    if (this.parkedResponse || this.ttsActive || this.absenceCheckinInFlight || this.absenceWantsToActNow()) {
      return; // ausencia (o una narración/respuesta ya en curso) gana — reintenta en el próximo tick del scorer
    }

    const action = vcLadderNextAction(state.level, this.vcStrikes);
    if (action === 'wait') return;

    if (action === 'suspend') {
      this.vcSuspending = true;
      // ponytail: si el candidato estaba a mitad de una respuesta NO enviada
      // a la pregunta actual, este forced-submit la descarta (mismo trade-off
      // que ABSENT_TRANSCRIPT ya acepta hoy) — "confirmed → suspender
      // directo" del contrato pide inmediatez, no esperar a que suelte el
      // piso. Lo YA sellado (preguntas previas) no se toca — eso es lo que
      // el contrato pide evaluar (§0).
      void this.submitAccumulated('conferencing_tool', CONFERENCING_TRANSCRIPT);
      return;
    }

    // action === 'warn'
    void this.speakConferencingWarning(this.vcStrikes);
  }

  /** Habla el AVISO 1 o 2 (según cuántos strikes ya hay bancados) reusando la
   *  MISMA chrome de narración que absence check-in — interjección corta que
   *  no pisa la ventana de respuesta en curso ni la resetea. */
  private async speakConferencingWarning(strikesGiven: number): Promise<void> {
    if (this.turnFailed) return;
    this.vcWarning = true;
    const { text, graceS } = vcWarningCopy(strikesGiven);
    const assistantTurnId = `low-vc-warn-${++this.generation}`;
    const gen = this.generation;

    this.host.ingestCommand({
      type: 'assistant_turn_started',
      text: '',
      display_text: text,
      phase: 'ack',
      turn_kind: 'ack',
      questionIndex: this.lastQuestionIndex,
      assistant_turn_id: assistantTurnId,
    });

    this.host.setLocalAudioPlaying?.(true);
    this.beginTts(text, false);
    try {
      await this.deps.tts.speak(text);
    } catch {
      /* non-fatal */
    }
    this.endTts();

    if (this.stopped || gen !== this.generation) {
      this.host.setLocalAudioPlaying?.(false);
      this.vcWarning = false;
      return;
    }

    this.host.ingestCommand({
      type: 'assistant_logical_complete',
      assistant_turn_id: assistantTurnId,
      requires_playback_ack: false,
      questionIndex: this.lastQuestionIndex,
    });

    this.host.notifyPlaybackEnd();
    this.host.setLocalAudioPlaying?.(false);

    this.markAnswerWindowStart();
    this.host.ingestCommand({
      type: 'mic_open',
      thinking_time_s: this.micThinkSForResume(),
      answer_budget_s: this.lastBudgets.answer_s,
      turn_silence_s: this.lastTurnSilenceS,
      ...this.confirmAdvanceFlag(),
      questionIndex: this.lastQuestionIndex,
      assistant_turn_id: assistantTurnId,
    });

    this.vcGraceTimer = setTimeout(() => this.recheckVcGrace(), graceS * 1000);
  }

  /** Re-chequeo dirigido al expirar la gracia del aviso — contrato §2: "si el
   *  score bajó de 30 → vuelve a watch, NO cuenta strike". */
  private recheckVcGrace(): void {
    this.vcGraceTimer = null;
    if (this.pauseActive && !this.stopped) {
      // Pausa: se reintenta al terminar, sin aviso mientras tanto.
      this.vcGraceTimer = setTimeout(() => this.recheckVcGrace(), 1000);
      return;
    }
    this.vcWarning = false;
    if (this.stopped || this.vcSuspending) return;

    const currentScore = this.deps.vcScorer?.state.score ?? 0;
    if (vcRecheckClearsStrike(currentScore)) {
      // ponytail: cooldown simple en vez de esperar a que VcScorerService baje
      // su propio `level` fuera de 'suspect' — esa salida pide 120s SOSTENIDOS
      // bajo score 30 (bastante más que nuestra gracia de 30/60s). Sin este
      // cooldown, el próximo tick del scorer (<=5s) vería `level` TODAVÍA en
      // 'suspect' (histéresis propia del scorer) y re-armaría el MISMO aviso
      // en loop. Reintentar recién después de la MISMA ventana de gracia que
      // se acaba de usar alcanza para no molestar de más y es fácil de
      // razonar — mejorarlo exigiría espiar el estado interno del scorer.
      const graceS = this.vcStrikes === 0 ? VC_GRACE_FIRST_S : VC_GRACE_SECOND_S;
      this.vcQuietUntilMs = Date.now() + graceS * 1000;
      return;
    }

    this.vcStrikes += 1;
    this.persistVcStrikes();
    if (this.vcStrikes >= VC_MAX_STRIKES) {
      this.vcSuspending = true;
      void this.submitAccumulated('conferencing_tool', CONFERENCING_TRANSCRIPT);
      return;
    }
    // Si todavía no llegó a maxStrikes, queda idle: el próximo emit reactivo
    // de vcScorer.state$ (<=5s, VcScorerService reemite igual sin cambios)
    // vuelve a llamar a handleVcState y esta vez escala al AVISO 2.
  }

  /**
   * Narra la despedida de suspensión (texto LITERAL del contrato, devuelto
   * por fc-low-api o el fallback local si la llamada falló) y cierra la
   * sesión con el MISMO mapeo que usa el agente Python en HIGH: `reason`
   * queda en 'aborted', el motivo real viaja en `termination_reason` — así
   * fc-session.component.ts dispara viewState='suspended' (pantalla neutra
   * ya agregada por otro agente) en vez de la de error genérico.
   */
  private async applyConferencingSuspend(res: FcLowResponse): Promise<void> {
    if (this.turnFailed) return;
    const farewell =
      (res.narration_text || res.display_text || VC_SUSPENSION_FALLBACK_TEXT).trim() ||
      VC_SUSPENSION_FALLBACK_TEXT;

    const gen = ++this.generation;
    const assistantTurnId = `low-vc-suspend-${gen}`;

    this.host.ingestCommand({
      type: 'assistant_turn_started',
      text: '',
      display_text: farewell,
      phase: 'farewell',
      turn_kind: 'farewell',
      assistant_turn_id: assistantTurnId,
    });

    this.host.setLocalAudioPlaying?.(true);
    this.beginTts(farewell, false);
    try {
      await this.deps.tts.speak(farewell);
    } catch {
      /* TTS errors are non-fatal — el candidato igual ve la pantalla de suspensión */
    }
    this.endTts();

    if (this.stopped || gen !== this.generation) {
      this.host.setLocalAudioPlaying?.(false);
      return;
    }

    this.host.ingestCommand({
      type: 'assistant_logical_complete',
      assistant_turn_id: assistantTurnId,
      requires_playback_ack: false,
    });
    this.host.notifyPlaybackEnd();
    this.host.setLocalAudioPlaying?.(false);

    if (!(await this.micGateOpen(gen))) return;
    this.host.ingestCommand({
      type: 'interview_complete',
      reason: 'aborted',
      termination_reason: 'conferencing_tool',
      assistant_turn_id: assistantTurnId,
    });
  }
}
